// Import
import i18n from '@/i18n'
import Vue from 'vue'
import store from '@/store'
import router from '@/router'
import { Api } from '@/core/api.js'
import { FORMAT, TOOLS } from '@/constants'

import { db_issues, db_tasks, db_tasks_queue, db_templates } from '@/database'
import { buildDefaultLocalKpiCounts, buildRadarModuleState, haveSameTemplateIds, setTemplatesForRadarKpi } from '@/store/utils/radarKpis.js'
import { buildItemCustomMapFromTemplates, mergeTemplateCustomIntoItem, getCustomApply } from '@/utils/templateCustom'
import { buildHierarchicalOrderedItems, getBaseOrder } from '@/store/utils/checklistItems'
import { postTaskWithRetry, shouldQueueOfflineSync } from '@/utils/taskApiRetry'
import { getLocationMoment, toLocationWallClockMoment } from '@/utils/locationTime'

/* global compare */

const api = new Api()

/** Key del KPI "Por justificar de ayer" en RADAR_MODULE_SCHEMA (src/store/utils/radarSchema.js). */
const RADAR_KPI_TASKS_TO_JUSTIFY_YESTERDAY = 'tasks_to_justify_yesterday'

/** Key del KPI "Por justificar hoy" en RADAR_MODULE_SCHEMA (BUILD-7281). 100% local: no viene del API. */
const RADAR_KPI_TASKS_TO_JUSTIFY_TODAY = 'tasks_to_justify_today'

/** Key del KPI "Tareas que vencen pronto" en RADAR_MODULE_SCHEMA (BUILD-7290). 100% local: no viene del API. */
const RADAR_KPI_TASKS_EXPIRING_SOON = 'tasks_expiring_soon'

/**
 * Cada cuánto se recalculan en segundo plano los KPIs locales de Radar (tasks_to_justify_today,
 * tasks_expiring_soon), para detectar checklists que entran/salen de esos KPIs únicamente por el
 * paso del tiempo, sin ninguna acción del usuario (BUILD-7281, BUILD-7290).
 */
const LOCAL_RADAR_KPIS_WATCHER_INTERVAL_MS = 60 * 1000

/** Id del setInterval del watcher de arriba; module-scoped (no en el state) porque no es dato serializable de UI. */
let localRadarKpisWatcherIntervalId = null

/**
 * BUILD-7404: contador de generación de las cargas del KPI "por justificar ayer". Cada carga captura su
 * número al empezar y solo escribe el resultado si sigue siendo la más reciente, para que una petición
 * antigua y lenta (p.ej. de loadRadarData) no pise con datos obsoletos el conteo de una más nueva.
 */
let tasksToJustifyYesterdayKpiRequestSeq = 0

/** Paso inicial de CALCSCORE: a ceros scores y alertas por timeframe afectado. */
function resetTimeframesForCalcScore(timeframesData, checklistId, timeframeId, alertPendingCount, alertPendingShow, alertImportantShow) {
    Object.keys(timeframesData).forEach((currentTimeframeId) => {
        if (typeof checklistId === 'undefined' || currentTimeframeId == timeframeId) {
            const timeframe = timeframesData[currentTimeframeId]
            timeframe.completed = 0
            timeframe.total = 0
            timeframe.score = 0

            alertPendingCount[timeframe.id] = 0
            alertPendingShow[timeframe.id] = false
            alertImportantShow[timeframe.id] = false
            timeframe.alert_pending_count = 0
            timeframe.alert_pending_show = false
            timeframe.alert_important_show = false
        }
    })
}

/** Paso inicial de CALCSCORE: a ceros scores y alertas por categoría afectada. */
function resetCategoriesForCalcScore(aCategories, checklistId, categoryId, categoryAlertPendingCount, categoryAlertPendingShow, categoryAlertImportantShow) {
    Object.keys(aCategories).forEach((currentCategoryId) => {
        if (typeof checklistId === 'undefined' || currentCategoryId == categoryId) {
            const category = aCategories[currentCategoryId]
            category.completed = 0
            category.total = 0
            category.score = 0

            categoryAlertPendingCount[category.id] = 0
            categoryAlertPendingShow[category.id] = false
            categoryAlertImportantShow[category.id] = false
            category.alert_pending_count = 0
            category.alert_pending_show = false
            category.alert_important_show = false
        }
    })
}

/** Suma completed/total de una checklist a agregados de timeframe y categorías (incl. "all"). */
function addChecklistTotalsToTimeframeAndCategoryAggregates(checklist, timeframesData, aCategories) {
    timeframesData[checklist.timeframe_id].completed += checklist.completed
    timeframesData[checklist.timeframe_id].total += checklist.total

    if (checklist.category_id && aCategories[checklist.category_id]) {
        aCategories[checklist.category_id].completed += checklist.completed
        aCategories[checklist.category_id].total += checklist.total
    }
    if (aCategories['all']) {
        aCategories['all'].completed += checklist.completed
        aCategories['all'].total += checklist.total
    }
}

/**
 * Reconstruye checklist.items con inserción ordenada por item.order (tras mutar órdenes en el bucle de ítems).
 * Solo aplica a checklists recalculadas por completo en esta pasada de CALCSCORE.
 */
function reorderChecklistItemsByOrderValue(checklist) {
    const keys = Object.keys(checklist.items).sort((a, b) => {
        return parseFloat(checklist.items[a].order) - parseFloat(checklist.items[b].order)
    })
    const itemsById = {}
    for (let i = 0; i < keys.length; i++) {
        const item = checklist.items[keys[i]]
        itemsById[item.item_id] = item
    }
    return itemsById
}

/**
 * Visibilidad de subítems según condición vs valor del ítem padre (CALCSCORE).
 */
function subitemConditionShows(parentConditionItem, conditionValue, parentItemValue, conditionOperator) {
    if (conditionOperator === 'true' && conditionValue === 'true') {
        return true
    }

    let show = false
    const parentItem = parentConditionItem || null

    switch (parentConditionItem.type) {
        case FORMAT.CHECK:
            show = compare(conditionValue, parentItemValue, conditionOperator)
            break
        case FORMAT.EMAIL:
        case FORMAT.DOCUMENT:
        case FORMAT.YESNO:
            show = compare(parseInt(conditionValue) == 1 || (conditionValue == '' && conditionOperator == '!empty'), parentItemValue == true || parentItemValue == 'true' || (parentItemValue != null && conditionValue == '' && conditionOperator == '!empty'), conditionOperator)
            break
        case FORMAT.YESNONC:
            show = compare(parseInt(conditionValue) || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue || (conditionValue === '' && conditionOperator === '!empty'), conditionOperator)
            break
        case FORMAT.MULTIPLE: {
            const parentItemIsMultiselect = parentItem && parentItem.config && parentItem.config.hasOwnProperty('multiselect') && parseInt(parentItem.config.multiselect) === 1

            if (!parentItemIsMultiselect) {
                show = compare(conditionValue || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue, conditionOperator)
            } else if (Array.isArray(parentItemValue) || (typeof parentItemValue === 'string' && parentItemValue.includes('|'))) {
                const parentItemValues = Array.isArray(parentItemValue) ? parentItemValue : parentItemValue.split('|')
                show = parentItemValues.some((value) => compare(conditionValue, value, conditionOperator))
            } else {
                show = compare(conditionValue, parentItemValue, conditionOperator)
            }
            break
        }
        case FORMAT.TEXT:
        case FORMAT.TEXTAREA:
        case FORMAT.PHOTO:
        case FORMAT.DATE:
        case FORMAT.TIME:
        case FORMAT.DATETIME:
        case FORMAT.SIGNATURE:
            show = compare(conditionValue, parentItemValue, conditionOperator)
            break
        case FORMAT.NUMBER: {
            const aux = parentItemValue.toString().replace(',', '.')
            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
            break
        }
        case FORMAT.TEMPERATURE: {
            const aux = parentItemValue.toString().replace(',', '.')
            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
            break
        }
        default:
            break
    }

    return show
}

/**
 * Calcula visibilidad y orden para ítems con condiciones de subítem (CALCSCORE). No muta `item`.
 * @returns {{ is_visible: boolean|null, order: number }}
 */
function computeCalcScoreSubitemConditions(item, checklist) {
    const subitemConditionsData = item.conditions
    const conditionsCombinator = typeof subitemConditionsData.operator !== 'undefined' ? subitemConditionsData.operator : 'AND'
    const conditions = subitemConditionsData.values

    let shouldDisplay = null
    let parentItemOrder = item.parent_id && checklist.items[item.parent_id] ? parseFloat(checklist.items[item.parent_id].order) : -1

    for (const key in conditions) {
        let show = false

        const condition = conditions[key]
        const conditionOperator = condition && condition.operator ? condition.operator : '='
        const conditionValue = condition && typeof condition.value !== 'undefined' ? condition.value : false

        const parentConditionItem = checklist.items[key]
        const parentItem = parentConditionItem || null
        const parentItemValue = parentItem && typeof parentItem.value !== 'undefined' ? parentItem.value : null

        if (parentConditionItem && (parentItemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(parentConditionItem.type)) && parentConditionItem.is_visible) {
            show = subitemConditionShows(parentConditionItem, conditionValue, parentItemValue, conditionOperator)
        }
        if (shouldDisplay === null) {
            shouldDisplay = show
        } else if (conditionsCombinator === 'AND') {
            shouldDisplay = shouldDisplay && show
        } else {
            shouldDisplay = shouldDisplay || show
        }

        if (parseFloat(parentItemOrder) === -1 && parentConditionItem) {
            parentItemOrder = parseFloat(parentConditionItem.order)
        }
    }

    const subitemOrder = getBaseOrder(item)
    let order
    if (!Number.isNaN(parentItemOrder) && parentItemOrder !== -1 && !Number.isNaN(subitemOrder)) {
        order = parentItemOrder + subitemOrder / 10000
    } else {
        order = subitemOrder
    }

    return { is_visible: shouldDisplay, order }
}

/**
 * Si debe mostrarse una alerta según trigger y valor del ítem (CALCSCORE). El caller filtra is_visible, tipo email, etc.
 */
function itemAlertTriggerShows(item, alertValue, itemValue, operator) {
    if (operator === 'true' && alertValue === 'true') {
        return true
    }

    let show = false

    switch (item.type) {
        case FORMAT.CHECK:
            show = compare(alertValue, itemValue, operator)
            break
        case FORMAT.EMAIL:
        case FORMAT.DOCUMENT:
        case FORMAT.YESNO:
            show = compare(parseInt(alertValue) == 1 || (alertValue == '' && operator == '!empty'), itemValue == true || itemValue == 'true', operator)
            break
        case FORMAT.YESNONC:
            show = compare(parseInt(alertValue) || (alertValue == '' && operator == '!empty'), itemValue, operator)
            break
        case FORMAT.MULTIPLE: {
            const itemIsMultiselect = item && item.config && item.config.hasOwnProperty('multiselect') && parseInt(item.config.multiselect) === 1

            if (itemIsMultiselect && (Array.isArray(itemValue) || (typeof itemValue === 'string' && itemValue.includes('|')))) {
                const itemValues = Array.isArray(itemValue) ? itemValue : itemValue.split('|')
                show = itemValues.some((value) => compare(alertValue || (alertValue === '' && operator === '!empty'), value, operator))
            } else {
                show = compare(alertValue || (alertValue === '' && operator === '!empty'), itemValue, operator)
            }
            break
        }
        case FORMAT.TEXT:
        case FORMAT.TEXTAREA:
        case FORMAT.PHOTO:
        case FORMAT.DATE:
        case FORMAT.TIME:
        case FORMAT.DATETIME:
        case FORMAT.SIGNATURE:
            show = compare(alertValue, itemValue, operator)
            break
        case FORMAT.NUMBER: {
            const aux = itemValue.toString().replace(',', '.')
            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
            break
        }
        case FORMAT.TEMPERATURE: {
            const aux = itemValue.toString().replace(',', '.')
            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
            break
        }
        default:
            break
    }

    return show
}

/**
 * Construye el payload de alertas para CALCSCORE a partir de `item.alerts_triggers`.
 * Puede mutar `alert.class` en cada trigger (comportamiento previo).
 * @returns {{ preview: string, message: string, issues: string[], class: string[] }|null}
 */
function computeCalcScoreItemAlerts(item) {
    const alerts = item.alerts_triggers
    if (!alerts) {
        return null
    }

    const itemValue = item.value
    let message = ''
    let preview = ''
    const issues = []
    const classes = []

    for (const i in alerts) {
        const alert = alerts[i]
        const operator = alert && typeof alert.operator !== 'undefined' ? alert.operator : '='
        const alertValue = alert && typeof alert.value !== 'undefined' ? alert.value : false

        const alertDate = alert.date ? alert.date : ''
        let show = false

        if ((itemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(item.type)) && item.is_visible && alert.type && alert.type != 'email') {
            show = itemAlertTriggerShows(item, alertValue, itemValue, operator)
        }

        if (show && (alert.msg || alert.message)) {
            if (message === '') {
                message = alert.msg != '' ? alert.msg : alert.message
            } else {
                message = message + '<br/>' + (alert.msg !== '' ? alert.msg : alert.message)
            }

            if (preview === '') {
                preview = alert.msg !== '' ? alert.msg : alert.message
            } else {
                preview = preview + ', ' + (alert.msg != '' ? alert.msg : alert.message)
            }
        }

        if (show && alert.tpl) {
            issues.push(alert.tpl)
        }
        if (show && alert.class) {
            if (alert.class == 'alert') {
                alert.class = 'warning'
            }
            classes.push(alert.class)
        }

        if (show && alert.template) {
            if (message == '') {
                message = i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
            } else {
                message = message + '<br/>' + i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
            }

            if (preview == '') {
                preview = i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
            } else {
                preview = preview + ', ' + i18n.tc('tasks.generated_checklist', alertDate, { c: alertDate })
            }
        }
    }

    const oAlert = {
        preview: preview,
        message: message,
        issues: issues,
        class: classes
    }
    if (oAlert.message.length > 0 || oAlert.issues.length > 0) {
        return oAlert
    }
    return null
}

/**
 * Deltas de scoring por ítem para CALCSCORE (solo lectura de `item`; no muta checklist).
 * @returns {{ unanswered: number, completed: number, total: number }}
 */
function computeCalcScoreItemScoringDeltas(item, checklist) {
    const itemsExcluded = checklist.def.item.itemsExcluded
    const appliesToScore = ((!item.parent_id && itemsExcluded.indexOf(item.type) == -1) || (item.parent_id && item.is_visible)) && !item.planned_to_date && Number(item.apply) > 0 && getCustomApply(item) !== 0

    if (!appliesToScore) {
        return { unanswered: 0, completed: 0, total: 0 }
    }

    let unanswered = 0
    let completed = 0

    switch (item.type) {
        case FORMAT.YESNO:
        case FORMAT.YESNONC:
        case FORMAT.CHECK:
        case FORMAT.TEXT:
        case FORMAT.PHOTO:
        case FORMAT.NUMBER:
        case FORMAT.DATE:
        case FORMAT.TEXTAREA:
        case FORMAT.MULTIPLE:
        case FORMAT.TIME:
        case FORMAT.DATETIME:
        case FORMAT.SIGNATURE:
        case FORMAT.TEMPERATURE:
        case FORMAT.EMAIL:
        case FORMAT.DOCUMENT:
            if (item.complete_date == false && item.apply) {
                unanswered = 1
            } else if (item.require_review) {
                completed = item.review ? 1 : 0.5
            } else {
                completed = 1
            }
            break
        default:
            break
    }

    const total = ![FORMAT.LINK, FORMAT.RESOURCE].includes(item.type) ? 1 : 0

    return { unanswered, completed, total }
}

/**
 * Si la checklist requiere justificación por ventana límite vs ahora (CALCSCORE). No muta estado.
 * @returns {{ needJustification: boolean, bumpPendingShow: boolean }} bumpPendingShow solo si hace falta mostrar alerta pendiente (!justify_id).
 */
function computeCalcScoreNeedJustification(now, checklist, limitStart, limitEnd) {
    const needJustification = !now.isBetween(limitStart, limitEnd) && parseInt(checklist.score, 10) < 100 && now.isAfter(limitEnd) && checklist.completed != checklist.total

    return {
        needJustification,
        bumpPendingShow: needJustification && !checklist.justify_id
    }
}

/**
 * Un checklist está justificado cuando tiene usuario y fecha de justificación (ver definición
 * funcional del ticket BUILD-7280). No se usa `justify_id` porque puede ser un id de motivo
 * "falsy" válido (p.ej. 0) y daría un falso negativo.
 *
 * Se comprueba con `!!` (verdadero real), no `!== false`: si el checklist viene de una fuente
 * donde estos campos no existen (p.ej. datos offline/caché antiguos), serían `undefined`, y
 * `undefined !== false` da `true`, tratándolo como justificado por error.
 */
function isChecklistJustified(checklist) {
    return !!checklist.justify_user_id && !!checklist.justify_date
}

/** "Hoy" según el reloj operacional del local. */
function getTodayBusinessDateKey(rootGetters) {
    return getLocationMoment(rootGetters).format('YYYY-MM-DD')
}

/** "Ayer" según el mismo criterio que usa el backend para tasks_to_justify_yesterday (RadarModel::getTaskTemplatesToJustifyByBusinessDate). */
function getYesterdayBusinessDateKey(rootGetters) {
    return getLocationMoment(rootGetters).subtract(1, 'day').format('YYYY-MM-DD')
}

/** Igual que `getYesterdayBusinessDateKey`, aplicado al `business_date` de un checklist en concreto. */
function getChecklistBusinessDateKey(checklist) {
    if (!checklist || !checklist.business_date) {
        return null
    }

    return moment.utc(checklist.business_date).format('YYYY-MM-DD')
}

/**
 * BUILD-7281: evalúa "¿necesita justificación AHORA MISMO?" para cualquier checklist ya cargado en
 * `items`, sin depender de que el `calcScore` en curso lo haya recorrido: cuando calcScore recibe
 * un `checklistId` puntual, solo ese checklist pasa por el bloque que recalcula `need_justification`
 * (ver más abajo, `if (typeof checklistId === 'undefined' || currentChecklistId === checklistId)`);
 * el resto conserva el `need_justification` que tenía la última vez que SÍ se recalculó, que puede
 * haber quedado vencido (stale) por el simple paso del tiempo aunque nada en ese checklist cambió.
 *
 * `limit`/`score`/`completed`/`total` sí siguen siendo válidos de reutilizar aunque este checklist
 * no se haya tocado en el calcScore actual: no cambian salvo por una acción real sobre ese
 * checklist (responder, completar, justificar), y esa acción siempre dispara su propio calcScore.
 * Lo único que puede quedar desfasado es la comparación contra "ahora", así que se recalcula aquí
 * con el mismo reloj operacional del local que usa calcScore para no introducir un segundo
 * criterio de "vencido".
 */
function checklistNeedsJustificationNow(checklist, rootGetters) {
    if (!checklist || !checklist.limit || !checklist.limit.start || !checklist.limit.end) {
        return false
    }

    const now = getLocationMoment(rootGetters).set({ s: 0, ms: 0 })
    const limitStart = moment.utc(checklist.limit.start)
    const limitEnd = moment.utc(checklist.limit.end)

    return computeCalcScoreNeedJustification(now, checklist, limitStart, limitEnd).needJustification
}

/**
 * BUILD-7281: templates de HOY pendientes de justificar (vencidos, sin completar y sin
 * justificar), para el KPI 100% local "tasks_to_justify_today". Usa `checklistNeedsJustificationNow`
 * (en vez del `need_justification` ya guardado en el checklist) precisamente para no depender de
 * qué checklist tocó el último calcScore, e `isChecklistJustified`, igual que tasks_to_justify_yesterday.
 *
 * A diferencia de esa KPI (que se actualiza de forma incremental porque puede incluir templates
 * de checklists no cargados en `items`), esta se recalcula completa desde `items` cada vez: al ser
 * 100% local no hay riesgo de desincronizarse con checklists no cargados, y así se evita duplicar
 * la lógica de "¿queda otro checklist pendiente del mismo template?".
 */
function computeTasksToJustifyTodayTemplateIds(items, rootGetters) {
    const todayBusinessDateKey = getTodayBusinessDateKey(rootGetters)
    const templateIds = new Set()

    Object.values(items).forEach((checklist) => {
        const businessDateKey = getChecklistBusinessDateKey(checklist)

        if (businessDateKey === todayBusinessDateKey && checklistNeedsJustificationNow(checklist, rootGetters) && !isChecklistJustified(checklist)) {
            templateIds.add(checklist.template_id)
        }
    })

    return Array.from(templateIds)
}

/** Minutos de antelación de "vence pronto" para el KPI tasks_expiring_soon (BUILD-7290). */
const TASKS_EXPIRING_SOON_WINDOW_MINUTES = 60

/**
 * BUILD-7290: evalúa "¿vence pronto AHORA MISMO?" para cualquier checklist ya cargado en `items`:
 * faltan 60 minutos o menos para `limit.end`, pero todavía no venció (`now < limit.end`). Al ser
 * una función pura sobre `checklist.limit` (fijado desde la carga, ver más abajo) y el reloj
 * actual, se puede evaluar en cualquier momento sin depender de qué checklist tocó el último
 * calcScore, igual que `checklistNeedsJustificationNow` para tasks_to_justify_today. Usa el mismo
 * el reloj operacional del local que usa calcScore, para no introducir otro criterio de "ahora".
 */
function checklistExpiresSoonNow(checklist, rootGetters) {
    if (!checklist || !checklist.limit || !checklist.limit.end) {
        return false
    }

    const now = getLocationMoment(rootGetters).set({ s: 0, ms: 0 })
    const limitEnd = moment.utc(checklist.limit.end)
    const minutesToExpire = limitEnd.diff(now, 'minutes', true)

    return minutesToExpire > 0 && minutesToExpire <= TASKS_EXPIRING_SOON_WINDOW_MINUTES
}

/**
 * BUILD-7290: templates de HOY que "vencen pronto" (dentro de la ventana de 60 minutos antes de
 * `limit.end`, sin completar y con ítems visibles aún pendientes de responder), para el KPI 100%
 * local "tasks_expiring_soon". `checklist.unanswered` (calculado por calcScore) no sufre el mismo
 * problema de staleness que `need_justification`: solo cambia al responder/completar un ítem, una
 * acción que siempre dispara su propio calcScore, así que sigue siendo válido leerlo aquí aunque
 * el checklist no se haya tocado en el calcScore actual.
 */
function computeTasksExpiringSoonTemplateIds(items, rootGetters) {
    const todayBusinessDateKey = getTodayBusinessDateKey(rootGetters)
    const templateIds = new Set()

    Object.values(items).forEach((checklist) => {
        const businessDateKey = getChecklistBusinessDateKey(checklist)

        if (businessDateKey === todayBusinessDateKey && !checklist.complete_date && checklist.unanswered > 0 && checklistExpiresSoonNow(checklist, rootGetters)) {
            templateIds.add(checklist.template_id)
        }
    })

    return Array.from(templateIds)
}

/**
 * BUILD-7429: ¿este checklist concreto debe mostrar el tag "Vence pronto" AHORA MISMO? Reutiliza el
 * mismo criterio de "hoy" (getChecklistBusinessDateKey/getTodayBusinessDateKey) y de ventana
 * (checklistExpiresSoonNow) que el KPI local tasks_expiring_soon (BUILD-7290), descartando además
 * los checklists ya completados. Se evalúa por checklist (no por plantilla) para que el tag refleje
 * exactamente la card que vence pronto y desaparezca al completarse, vencer o dejar de ser de hoy.
 */
function checklistShowsExpiringSoonTag(checklist, rootGetters) {
    if (!checklist || checklist.complete_date) {
        return false
    }

    const businessDateKey = getChecklistBusinessDateKey(checklist)

    return businessDateKey === getTodayBusinessDateKey(rootGetters) && checklistExpiresSoonNow(checklist, rootGetters)
}

/**
 * BUILD-7404: ¿el checklist tiene al menos un ítem que puntúa? Es decir, `total > 0` en términos de
 * calcScore. Sin esto, un checklist "vacío" (solo enlaces/recursos o sin ítems aplicables) tendría
 * `completed == total == 0` y calcScore NO lo marca como pendiente, pero `!complete_date` sí lo haría,
 * inflando el KPI. Se replica el núcleo de `computeCalcScoreItemScoringDeltas` (tipo puntuable, aplica y
 * no planificado); no se replican los matices de visibilidad/condiciones de subítems, de impacto marginal.
 */
function checklistHasScorableItems(checklist) {
    const items = checklist && checklist.items

    if (!items) {
        return false
    }

    return Object.values(items).some((item) => {
        return item && ![FORMAT.LINK, FORMAT.RESOURCE].includes(item.type) && Number(item.apply) > 0 && !item.planned_to_date
    })
}

/**
 * BUILD-7404: ¿este checklist está pendiente de justificar? Su ventana `limit` ya venció, tiene ítems que
 * puntúan (`total > 0`), no está completado y no está justificado. Equivale al criterio de calcScore
 * (`computeCalcScoreNeedJustification`: vencido, `score < 100` y `completed != total`): `!complete_date`
 * —marcador persistido que calcScore fija/limpia según el score— cubre `score < 100`/`completed != total`
 * para `total > 0`, y `checklistHasScorableItems` descarta el caso `total == 0`. Se apoya en estos campos
 * (y no en el score) para poder evaluarlo también sobre los checklists de ayer cargados en segundo plano,
 * que no pasan por calcScore y por tanto no traen score.
 */
function isChecklistPendingJustification(checklist, rootGetters) {
    if (!checklist || !checklist.limit || !checklist.limit.end) {
        return false
    }

    if (checklist.complete_date || isChecklistJustified(checklist) || !checklistHasScorableItems(checklist)) {
        return false
    }

    const now = getLocationMoment(rootGetters).set({ s: 0, ms: 0 })

    return now.isAfter(moment.utc(checklist.limit.end))
}

/**
 * BUILD-7404: ids de las PLANTILLAS (template_id) distintas con al menos un checklist de AYER pendiente
 * de justificar. El estado `radarKPIs` guarda —igual que el resto de KPIs de Radar (tasks_to_justify_today,
 * tasks_expiring_soon) y que el backend (RadarModel::getTaskTemplatesToJustifyByBusinessDate,
 * SELECT DISTINCT ch.template_id)— un listado de template ids, NO de checklists; otras partes del Radar
 * dependen de que `templates` contenga template ids. Se usa tanto sobre los checklists de ayer cargados en
 * segundo plano para el Radar como sobre `state.items` cuando el usuario está viendo la lista de ayer.
 */
function computeTasksToJustifyYesterdayTemplateIds(items, rootGetters) {
    const yesterdayBusinessDateKey = getYesterdayBusinessDateKey(rootGetters)
    const templateIds = new Set()

    Object.values(items).forEach((checklist) => {
        if (getChecklistBusinessDateKey(checklist) === yesterdayBusinessDateKey && isChecklistPendingJustification(checklist, rootGetters)) {
            templateIds.add(checklist.template_id)
        }
    })

    return Array.from(templateIds)
}

/**
 * BUILD-7404: normaliza la respuesta cruda de `checklist/<type>/<fecha>` (indexada por franja horaria,
 * con `templates` = checklists) a una lista plana de checklists con los campos mínimos ya convertidos a
 * moment que necesita el cálculo del KPI de "por justificar ayer". Incluye `template_id` porque el KPI se
 * agrupa por plantilla. No pasa por `format`/`calcScore` para no tocar `state.items` (que contiene los
 * checklists de hoy que usa el resto de la app).
 */
function normalizeRawChecklistsForYesterdayKpi(rawData, rootGetters) {
    const checklists = []

    Object.values(rawData || {}).forEach((timeframe) => {
        if (!timeframe || !timeframe.templates) {
            return
        }

        Object.values(timeframe.templates).forEach((checklist) => {
            if (!checklist) {
                return
            }

            checklists.push({
                id: checklist.id,
                template_id: checklist.template_id,
                business_date: checklist.business_date ? toLocationWallClockMoment(rootGetters, checklist.business_date) : false,
                complete_date: checklist.complete_date ? toLocationWallClockMoment(rootGetters, checklist.complete_date) : false,
                justify_user_id: checklist.justify_user_id,
                justify_date: checklist.justify_date,
                limit: checklist.limit ? { end: checklist.limit.end ? toLocationWallClockMoment(rootGetters, checklist.limit.end) : false } : null,
                items: checklist.items
            })
        })
    })

    return checklists
}

/** BUILD-7404: ¿la colección normalizada contiene algún checklist de la fecha (business_date) indicada? */
function hasChecklistsForBusinessDate(checklists, businessDateKey) {
    return checklists.some((checklist) => getChecklistBusinessDateKey(checklist) === businessDateKey)
}

/** BUILD-7404: lee todas las franjas horarias cacheadas en la base local (db_tasks), indexadas por id. */
async function readCachedChecklistsFromLocalDb() {
    const timeframesById = {}

    await db_tasks.iterate((value) => {
        if (value && (value.id || value.id === 0)) {
            timeframesById[value.id] = value
        }
    })

    return timeframesById
}

/**
 * BUILD-7404: fuente de datos de los checklists de AYER para el KPI. Online se piden frescos al backend
 * (set completo y fiable); offline se leen de la base local (db_tasks), que puede estar incompleta o ser
 * de otra fecha —en ese caso el conteo saldrá menor u oculto—, priorizando mostrar un valor aproximado
 * antes que ninguno. En ambos casos la respuesta viene indexada por franja horaria con `templates`.
 */
async function loadYesterdayChecklistsSource(state, rootGetters, isOffline) {
    if (isOffline) {
        return readCachedChecklistsFromLocalDb()
    }

    const yesterdayDate = getYesterdayBusinessDateKey(rootGetters)
    const response = await api.get('checklist/' + state.type + '/' + yesterdayDate, {})

    return response && response.status && response.data ? response.data : null
}

/**
 * `setRadarKPIs` reemplaza radarKPIs completo con la respuesta del API /radar, incluido un reset a
 * `null` antes de cada carga (ver radar.js `clearRadarApiModuleData`). Los KPIs de `localKpiKeys`
 * (tasks_to_justify_yesterday BUILD-7404, tasks_to_justify_today BUILD-7281, tasks_expiring_soon
 * BUILD-7290) se calculan 100% en el frontend y NUNCA
 * deben depender del API para esas keys: por eso primero se quita cualquier entrada suya que venga
 * en la respuesta (hoy el API no las manda, pero si en el futuro empezara a hacerlo, no debe pisar
 * el cálculo local hasta el próximo calcScore) y luego, si ya había un valor local calculado antes
 * de este reset, se conserva. El siguiente calcScore ya se encarga de recalcularlos con datos frescos.
 */
function preserveLocalRadarKpis(previousRadarKPIs, nextRadarKPIs, localKpiKeys) {
    const baseNextRadarKPIs = Array.isArray(nextRadarKPIs) ? nextRadarKPIs : []
    const nextRadarKPIsWithoutLocalKpis = baseNextRadarKPIs.filter((kpi) => !kpi || !localKpiKeys.includes(kpi.key))

    const previousLocalKpis = Array.isArray(previousRadarKPIs) ? previousRadarKPIs.filter((kpi) => kpi && localKpiKeys.includes(kpi.key)) : []

    return [...nextRadarKPIsWithoutLocalKpis, ...previousLocalKpis]
}

/** Recalcula el listado `templates` de un KPI 100% local y lo persiste solo si cambió (evita commits redundantes cada vez que corre el watcher). */
function refreshLocalRadarKpiTemplates(state, commit, kpiKey, nextTemplates) {
    const currentKpi = Array.isArray(state.radarKPIs) ? state.radarKPIs.find((kpi) => kpi && kpi.key === kpiKey) : null

    if (!haveSameTemplateIds(currentKpi ? currentKpi.templates : [], nextTemplates)) {
        commit('setLocalRadarKpiTemplates', { kpiKey, templates: nextTemplates })
    }
}

/** Recalcula tasks_to_justify_today desde `items` (BUILD-7281). */
function refreshTasksToJustifyTodayKpi(state, commit, rootGetters) {
    refreshLocalRadarKpiTemplates(state, commit, RADAR_KPI_TASKS_TO_JUSTIFY_TODAY, computeTasksToJustifyTodayTemplateIds(state.items, rootGetters))
}

/** Recalcula tasks_expiring_soon desde `items` (BUILD-7290). */
function refreshTasksExpiringSoonKpi(state, commit, rootGetters) {
    refreshLocalRadarKpiTemplates(state, commit, RADAR_KPI_TASKS_EXPIRING_SOON, computeTasksExpiringSoonTemplateIds(state.items, rootGetters))
}

/** BUILD-7404: recalcula tasks_to_justify_yesterday (plantillas distintas con checklists de ayer por justificar) desde una colección de checklists. */
function refreshTasksToJustifyYesterdayKpi(state, commit, items, rootGetters) {
    refreshLocalRadarKpiTemplates(state, commit, RADAR_KPI_TASKS_TO_JUSTIFY_YESTERDAY, computeTasksToJustifyYesterdayTemplateIds(items, rootGetters))
}

/**
 * BUILD-7404: actualiza el KPI "por justificar ayer" tras justificar un checklist de ayer. Si se está
 * viendo la lista de ayer (`state.items` es el set completo de ayer) se recalcula en el acto —también
 * offline—; en caso contrario se recarga en segundo plano para no contar un subconjunto incompleto.
 */
function updateYesterdayKpiAfterJustify(context, state, rootGetters, yesterdayBusinessDateKey) {
    if (state.date === yesterdayBusinessDateKey) {
        // Invalida cualquier carga en vuelo para que no pise después este recálculo ya actualizado.
        tasksToJustifyYesterdayKpiRequestSeq++
        refreshTasksToJustifyYesterdayKpi(state, context.commit, state.items, rootGetters)

        return
    }

    context.dispatch('loadTasksToJustifyYesterdayKpi')
}

/**
 * BUILD-7404: `tasks_to_justify_today` (BUILD-7281) y `tasks_expiring_soon` (BUILD-7290) son KPIs de
 * HOY que se calculan escaneando `state.items`, pero `items` solo contiene los checklists de la fecha
 * actualmente cargada. Al abrir "Por justificar ayer" desde el Radar, `items` pasa a contener checklists
 * de AYER, así que recalcular estos KPIs daría un falso 0 y desaparecerían del Radar hasta recargar. Por
 * eso solo se recalculan cuando la fecha cargada (`state.date`) es HOY —incluido el caso de que hoy no
 * tenga checklists, en el que sí deben ponerse a 0—; si la fecha cargada es otra se conserva el último
 * valor válido y se refrescará al volver a cargar los checklists de hoy (navegar a hoy, recargar o el
 * watcher periódico). Se usa `state.date` (fecha cargada) en vez de escanear `items` para no dejar sin
 * actualizar un "hoy" vacío ni recalcular con checklists nocturnos de ayer presentes en la vista de hoy.
 */
function refreshLocalRadarKpisFromItems(state, commit, rootGetters) {
    if (state.date !== getTodayBusinessDateKey(rootGetters)) {
        return
    }

    refreshTasksToJustifyTodayKpi(state, commit, rootGetters)
    refreshTasksExpiringSoonKpi(state, commit, rootGetters)
}

/**
 * BUILD-7281/BUILD-7290: arranca (una única vez) el watcher que recalcula periódicamente los KPIs
 * locales de Radar, para detectar en tiempo real cuando un checklist de hoy "vence" o entra/sale
 * de la ventana de "vence pronto" únicamente por el paso del tiempo, sin ninguna acción del
 * usuario. Se detiene en resetState (logout).
 */
function ensureLocalRadarKpisWatcherStarted(dispatch) {
    if (localRadarKpisWatcherIntervalId) {
        return
    }

    localRadarKpisWatcherIntervalId = setInterval(() => {
        dispatch('calcScore', { checklistId: undefined, itemId: undefined })
    }, LOCAL_RADAR_KPIS_WATCHER_INTERVAL_MS)
}

/** Detiene el watcher de arriba (ver ensureLocalRadarKpisWatcherStarted). */
function stopLocalRadarKpisWatcher() {
    if (localRadarKpisWatcherIntervalId) {
        clearInterval(localRadarKpisWatcherIntervalId)
        localRadarKpisWatcherIntervalId = null
    }
}

/** Tras el bucle de checklists: vuelca mapas de alertas a timeframes y calcula score del timeframe. */
function applyAlertMapsToTimeframesData(timeframesData, checklistId, timeframeId, alertPendingCount, alertPendingShow, alertImportantShow) {
    Object.keys(timeframesData).forEach((currentTimeframeId) => {
        const tframe = timeframesData[currentTimeframeId]
        if (typeof checklistId === 'undefined' || currentTimeframeId == timeframeId) {
            tframe.alert_pending_count = alertPendingCount[tframe.id]
            tframe.alert_pending_show = alertPendingShow[tframe.id]
            tframe.alert_important_show = alertImportantShow[tframe.id]

            tframe.score = parseInt(tframe.total) == 0 ? 0 : Math.floor((parseFloat(tframe.completed) * 100) / parseInt(tframe.total))
        }
    })
}

/** Tras el bucle de checklists: vuelca mapas de alertas a categorías y calcula score por categoría. */
function applyAlertMapsToCategories(aCategories, checklistId, categoryId, categoryAlertPendingCount, categoryAlertPendingShow, categoryAlertImportantShow) {
    Object.keys(aCategories).forEach((currentCategoryId) => {
        const category = aCategories[currentCategoryId]
        if (typeof checklistId === 'undefined' || currentCategoryId == categoryId || currentCategoryId == 'all') {
            category.alert_pending_count = categoryAlertPendingCount[category.id]
            category.alert_pending_show = categoryAlertPendingShow[category.id]
            category.alert_important_show = categoryAlertImportantShow[category.id]

            category.score = parseInt(category.total) == 0 ? 0 : Math.floor((parseFloat(category.completed) * 100) / parseInt(category.total))
        }
    })
}

// Triquiñuela para resetear el state
const getDefaultState = () => {
    return {
        stats: {
            alerts: {},
            activity: {}
        },
        items: {},
        current: false,
        templates: {},
        templatesLoaded: false, // needed by Radar
        timeframes: {
            current: 0,
            data: {}
        },
        date: false,
        type: 1,
        config: {
            date_format: 'YYYY-MM-DD',
            hour_format: 'HH:mm',
            datetime_format: 'YYYY-MM-DD HH:mm',
            has_score: true,
            has_state: true,
            has_timing: true,
            has_mandatory: false,
            is_autosave: true,
            can_create: false,
            item_search: false,
            has_calendar: true,
            temporal_view: false,
            close_when_completed: false,
            auth_users: {
                pin: 2,
                review: 2,
                task: 1
            },
            can_edit: {
                title: true,
                state: false,
                inPast: false,
                inTime: true,
                inFuture: true
            },
            item: {
                itemsTitle: [FORMAT.TITLE, FORMAT.SUBTITLE],
                itemsImage: [FORMAT.PHOTO, FORMAT.SIGNATURE],
                itemsInfo: [FORMAT.DOCUMENT, FORMAT.MESSAGE],
                itemsExcluded: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.DOCUMENT, FORMAT.MESSAGE, FORMAT.LINK, FORMAT.RESOURCE],
                itemsVisibles: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.MESSAGE],
                can_pospone: true,
                rate: false, // Esto es por si van a tener puntuaciones en funcion de lo que se responda
                can_review: true,
                has_periodicity: true,
                has_score: false
            }
        },
        current_date: null,
        user_date_changed: false,
        categories: {},
        isUserTyping: false,
        last_load_user_action: false,
        last_load_categories_user_action: false,
        radarKPIs: null
    }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
    getAll: (state, getters, rootState) => {
        return state.items
    },
    getById: (state, getters, rootState) => (id) => {
        return id ? state.items[id] : false
    },
    getItemsExcluded: (state, getters, rootState) => {
        return state.config.item.itemsExcluded
    },
    getConfig: (state, getters, rootState) => {
        return { ...state.config }
    },
    getTemplates: (state, getters, rootState) => {
        return state.templates
    },
    getTemplatesLoaded: (state) => {
        return state.templatesLoaded
    },
    getTemplateById: (state, getters, rootState, rootGetters) => (id) => {
        if (id == null) {
            return null
        }

        if (state.templates[id]) {
            return state.templates[id]
        }

        const items = state.items || {}

        for (const item of Object.values(items)) {
            if (item && String(item.template_id) === String(id)) {
                return item
            }
        }

        const templateFromStore = rootGetters['templates/getTemplateById'] ? rootGetters['templates/getTemplateById'](TOOLS.CHECKLISTS, id) : null

        if (templateFromStore) {
            return templateFromStore
        }

        return null
    },
    getSubtasks: (state, getters, rootState) => (check, task) => {
        let tasks = check ? state.items[check].items : {}
        let subtask = []

        Object.keys(tasks).map(function (item_id, i) {
            var item = tasks[item_id]
            if (item.parent_id == task) {
                subtask.push(item.item_id)
            }
        })

        return subtask
    },
    getItemsBySection: (state, getters, rootState) => (check, section) => {
        var newSectionFound = false
        var startLooking = false
        var resultItems = {}
        var items = check ? Object.values({ ...state.items[check].items }) : []

        // CLEAN OF SUBITEMS
        items = items.filter((obj) => {
            return !obj.parent_id
        })

        // FIRST SORT BY ORDER AND ADD ITEMS TO RESULT
        items
            .sort(function (l, r) {
                return l.order - r.order
            })
            .forEach(function (item) {
                if (item.type != 8 && startLooking && !newSectionFound) {
                    resultItems[item.item_id] = item
                } else {
                    if (!startLooking && item.item_id == section) {
                        startLooking = true
                    } else if (startLooking) {
                        newSectionFound = true
                        startLooking = false
                    }
                }
            })

        return resultItems
    },
    getTasks: (state, getters, rootState) => (id) => {
        let tasks = id ? state.items[id].items : {}
        let realTasks = []

        Object.keys(tasks).map(function (item_id, i) {
            var item = tasks[item_id]
            if (!item.parent_id) {
                realTasks.push(item)
            }
        })
        return realTasks
    },
    getTaskById: (state, getters, rootState) => (check_id, task_id) => {
        let tasks = state.items[check_id] ? state.items[check_id].items : {}
        let result = false

        Object.keys(tasks).map(function (item_id, i) {
            var item = tasks[item_id]

            if (item.item_id == task_id) {
                result = item
            }
        })
        return result
    },
    getAllStats: (state, getters, rootState) => {
        return state.stats
    },
    getStats: (state, getters, rootState) => (wich) => {
        return state.stats[wich]
    },
    getCurrentTimeframe: (state, getters, rootState) => {
        return state.timeframes.current
    },
    getTimeframes: (state, getters, rootState) => {
        return state.timeframes.data
    },
    getCheckTasksState: (state, getters, rootState) => (id) => {
        let items = id ? state.items[id].items : {}
        let values = []
        let result = ''

        Object.keys(items).map(function (id, i) {
            var val = items[id]

            if (val.type == 1 && !val.parent_id) {
                values.push(val.value ? 1 : 0)
            }
        })

        if (values.indexOf(0) != -1 && values.indexOf(1) != -1) {
            result = 'semichecked'
        } else if (values.indexOf(0) == -1) {
            result = 'checked'
        }

        return result
    },
    currentDate: (state, getters, rootState, rootGetters) => {
        return state.current_date || getTodayBusinessDateKey(rootGetters)
    },
    userDateChanged: (state, getters, rootState) => {
        return state.user_date_changed
    },
    getCategories: (state, getters, rootState) => (id) => {
        if (id) {
            return state.categories[id]
        }
        return state.categories
    },

    getNotesById: (state) => (id) => {
        if (state.items[id] && state.items[id].notes) {
            return state.items[id].notes
        }
        return state.items[id].notes
    },
    isUserTyping: (state) => {
        return state.isUserTyping
    },
    getLastLoadUserAction: (state) => {
        return state.last_load_user_action
    },
    getRadarModuleState: (state, getters, rootState, rootGetters) => (kpiKeys) => {
        const localKpiCounts = buildDefaultLocalKpiCounts(TOOLS.CHECKLISTS, kpiKeys)
        const result = buildRadarModuleState(TOOLS.CHECKLISTS, state.radarKPIs, kpiKeys, localKpiCounts, rootGetters)

        return result
    },
    /**
     * BUILD-7429: ids (como String) de las plantillas contabilizadas en el KPI local de Radar
     * "tasks_expiring_soon" (BUILD-7290). Es la misma fuente reactiva que mantiene el watcher de KPIs
     * locales, así que la vista de Tareas puede mostrar el tag "Vence pronto" de forma consistente con
     * ese KPI y reaccionar automáticamente cuando un checklist entra o sale de la ventana de vencimiento.
     */
    getExpiringSoonTemplateIds: (state) => {
        const kpi = Array.isArray(state.radarKPIs) ? state.radarKPIs.find((entry) => entry && entry.key === RADAR_KPI_TASKS_EXPIRING_SOON) : null

        return kpi && Array.isArray(kpi.templates) ? kpi.templates.map(String) : []
    },
    /** BUILD-7429: ¿el checklist indicado vence pronto ahora mismo? (mismo criterio que el KPI tasks_expiring_soon). */
    isChecklistExpiringSoon: (state, getters, rootState, rootGetters) => (checklist) => {
        return checklistShowsExpiringSoonTag(checklist, rootGetters)
    }
}

// actions
const actions = {
    resetState({ commit }) {
        stopLocalRadarKpisWatcher()
        commit('resetState')
    },
    loadCategories(context) {
        if (fnCheckConnection() && context.rootGetters.getFastConnection && (!state.last_load_categories_user_action || state.last_load_categories_user_action < moment().subtract(5, 'minutes').unix())) {
            context.commit('setLastLoadCategoriesUserAction', moment().unix())
            return api.get('checklist/1/categories').then(function (response) {
                var categories = response.data
                if (Object.values(response.data).length > 0) {
                    categories['all'] = {
                        id: 'all',
                        name: i18n.t('tasks.all_tasks'),
                        highlight: true,
                        order: -1,
                        color: '#374673',
                        type: 1
                    }
                }
                for (var k in categories) {
                    categories[k].score = 0
                    categories[k].total = 0
                    categories[k].alert_pending_count = 0
                    categories[k].alert_pending_show = false
                    categories[k].alert_important_show = false
                }

                context.commit('setCategories', categories)
            })
        }
    },
    loadAll({ state, context, commit, dispatch, rootState, rootGetters }, params) {
        var date = typeof params.date === 'string'
            ? params.date.substring(0, 10)
            : toLocationWallClockMoment(rootGetters, params.date).format('YYYY-MM-DD')

        // IF DATE IS TODAY AND USER HAS NOT LOADED ANY ACTION IN THE LAST 5 MINUTES LOAD IF NOT IGNORE
        if (process.env.VUE_APP_IS_APP !== 'true' && date == state.date && date == getTodayBusinessDateKey(rootGetters) && state.last_load_user_action && state.last_load_user_action > moment().subtract(1, 'minutes').unix()) {
            return
        }

        if ((typeof params.forceUpdate !== 'undefined' && params.forceUpdate) || date != state.date) {
            commit('setLastLoadUserAction', moment().unix())
            if (!fnCheckConnection()) {
                return loadTemplatesOffline(commit, dispatch, rootGetters)
            }

            let url = 'checklist/' + state.type + '/' + date
            return api
                .get(url, {})
                .then(function (response) {
                    if (!response.status) {
                        return loadTemplatesOffline(commit, dispatch, rootGetters)
                    }
                    _.forEach(_.values(response.data), async function (value, key) {
                        if (value.id !== 'undefined' && value !== 'undefined') {
                            await db_tasks.setItem(value.id, value)
                        }
                    })
                    commit('setDate', date)
                    return dispatch('mergeOffline', { data: response.data }).then(function (response2) {
                        return dispatch('format', { data: response2 }).then(() => {
                            return dispatch('calcScore', { checklistId: undefined, itemId: undefined })
                        })
                    })
                })
                .catch((error) => {
                    console.error('Error in loadAll():', error)
                    return loadTemplatesOffline(commit, dispatch, rootGetters)
                })
        } else {
            return loadTemplatesOffline(commit, dispatch, rootGetters)
        }
    },
    /**
     * BUILD-7404: calcula en el frontend el KPI de Radar "por justificar ayer" como el listado de
     * plantillas (template ids) distintas con algún checklist de ayer pendiente de justificar. Se cargan
     * aparte los checklists de ayer y se agrupan por plantilla aquí. No se usa `loadAll` a propósito:
     * `format` reemplazaría `state.items` (los de hoy que usa el resto de la app) por los de ayer.
     */
    async loadTasksToJustifyYesterdayKpi({ state, commit, rootGetters }) {
        const requestSeq = ++tasksToJustifyYesterdayKpiRequestSeq
        const isOffline = !fnCheckConnection()

        try {
            const rawChecklistsByTimeframe = await loadYesterdayChecklistsSource(state, rootGetters, isOffline)

            if (!rawChecklistsByTimeframe) {
                return
            }

            // Guard contra respuestas obsoletas: si empezó otra carga después de esta, no escribir.
            if (requestSeq !== tasksToJustifyYesterdayKpiRequestSeq) {
                return
            }

            const yesterdayChecklists = normalizeRawChecklistsForYesterdayKpi(rawChecklistsByTimeframe, rootGetters)

            // Offline la caché puede no contener checklists de ayer (p.ej. solo tiene los de hoy): en ese
            // caso no hay dato fiable, así que se conserva el último valor en vez de pisarlo con 0.
            if (isOffline && !hasChecklistsForBusinessDate(yesterdayChecklists, getYesterdayBusinessDateKey(rootGetters))) {
                return
            }

            commit('setLocalRadarKpiTemplates', {
                kpiKey: RADAR_KPI_TASKS_TO_JUSTIFY_YESTERDAY,
                templates: computeTasksToJustifyYesterdayTemplateIds(yesterdayChecklists, rootGetters)
            })
        } catch (error) {
            console.error('Error in loadTasksToJustifyYesterdayKpi():', error) // eslint-disable-line no-undef
        }
    },
    async addIssue(context, params) {
        await context.dispatch('calcScore', { checklistId: params.checklist_ref, itemId: undefined })

        var oData = { template: params.tpl_id }

        if (params.checklist_ref) {
            oData.checklist_ref = params.checklist_ref

            let check = context.getters.getById(params.checklist_ref)
            let total = check.total
            let answered = check.completed
            let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
            let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

            for (let id in check.items) {
                let item = check.items[id]

                if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                    if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                    if (!item.apply) resume.na++ // if item has not notes (no aplica)
                }
            }

            oData.total = total
            oData.answered = answered
            oData.score = score
            oData.resume = JSON.stringify(resume)
        }

        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }

        if (params.isOfflineCreated) {
            oData.isOfflineCreated = true
        }

        if (params.toolQueue) {
            oData.toolQueue = params.toolQueue
        }

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/add', oData).then(function (response) {
                var items = { ...state.items }
                var reference = { checklist: Object.values(response.data)[0].id, type: Object.values(response.data)[0].type, status: Object.values(response.data)[0].status }
                var item = { ...items[params.checklist_ref].items[params.item_ref] }
                item.reference = reference
                items[params.checklist_ref].items[params.item_ref] = item
                Vue.set(state, 'items', items)
                return response.data
            })
        } else {
            if (canWorkOffline) {
                const issue = await store.dispatch('issues/add', oData)

                const reference = {
                    checklist: issue.id,
                    type: issue.type,
                    status: issue.status
                }

                context.state.items[params.checklist_ref].items[params.item_ref].reference = reference
                const currentTimeframe = context.state.timeframes.current
                db_tasks.getItem(currentTimeframe.toString()).then(async (template) => {
                    if (template) {
                        template.templates[params.checklist_ref].items[params.item_ref].reference = reference
                        await db_tasks.setItem(currentTimeframe, template)
                    }
                })

                return issue
            }
        }
    },
    async comment(context, params) {
        const canWorkOffline = context.rootGetters.getOffline

        context.dispatch('calcScore', { checklistId: params.check, itemId: params.answer })

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 1, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        var sended = {
            action: 'comment',
            checklist: params.check,
            task: params.answer,
            message: params.message,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline,
            timeframe_id: state.timeframes.current
        }

        if (typeof params.file !== 'undefined') sended.file = params.file

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: sended, action: 'comment' })
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/comment', sended)

            if (!response.status && context.rootGetters.getOffline) {
                if (shouldQueueOfflineSync(response)) {
                    const offlineMessage = {
                        check: params.check,
                        answer: params.answer,
                        message: params.message,
                        requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
                    }
                    if (typeof params.file !== 'undefined') offlineMessage.image = params.file

                    return context
                        .dispatch('syncAction', sended)
                        .then(() => context.commit('setComment', offlineMessage))
                        .catch(() => context.commit('setComment', offlineMessage))
                }
                return context.dispatch('syncAction', sended)
            }

            if (!response.status) {
                var error = {
                    check: params.check,
                    answer: params.answer,
                    requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
                }
                if (typeof params.file !== 'undefined') error.image = null
                else error.message = ''

                context.commit('setComment', error)
                return { status: false }
            }

            var returned = {
                check: params.check,
                answer: params.answer,
                message: response.data.message,
                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
            }
            if (typeof response.data.image !== 'undefined') {
                returned.image = response.data.image
            }

            return context.commit('setComment', returned)
        } else if (context.rootGetters.getOffline) {
            let offlineMessage = {
                check: params.check,
                answer: params.answer,
                message: params.message,
                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
            }
            if (typeof params.file !== 'undefined') offlineMessage.image = params.file
            return context
                .dispatch('syncAction', sended)
                .then((response) => {
                    return context.commit('setComment', offlineMessage)
                })
                .catch(() => {
                    context.commit('setComment', offlineMessage)
                })
        } else {
            var error = {
                check: params.check,
                answer: params.answer,
                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
            }
            if (typeof params.file !== 'undefined') error.image = null
            else error.message = ''

            context.commit('setComment', error)
            return { status: false }
        }
    },
    async validate(context, params) {
        const canWorkOffline = context.rootGetters.getOffline
        context.commit('setValidation', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
        })

        await context.dispatch('calcScore', { checklistId: params.check, itemId: undefined })

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            timeframe_id: state.timeframes.current
        }

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'validate' })
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/task/validate', paramsApi)
                .then(function (response) {
                    if (!response.status && context.rootGetters.getOffline) {
                        paramsApi.action = 'validate'
                        return context
                            .dispatch('syncAction', paramsApi)
                            .then(() => {
                                return true
                            })
                            .catch(() => {
                                return true
                            })
                    } else if (!response.status) {
                        return context.commit('setReset', { check: params.check, answer: params.answer })
                    } else {
                        return true
                    }
                })
                .catch(() => {
                    if (context.rootGetters.getOffline) {
                        paramsApi.action = 'validate'
                        return context
                            .dispatch('syncAction', paramsApi)
                            .then(() => {
                                return true
                            })
                            .catch(() => {
                                return true
                            })
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer })
                    }
                })
        } else {
            if (context.rootGetters.getOffline) {
                paramsApi.action = 'validate'
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        return true
                    })
                    .catch(() => {
                        return true
                    })
            } else {
                context.commit('setReset', { check: params.check, answer: params.answer })
            }
        }
    },
    async unvalidate(context, params) {
        const canWorkOffline = context.rootGetters.getOffline
        context.commit('setUnvalidation', {
            check: params.check,
            answer: params.answer,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
        })
        await context.dispatch('calcScore', { checklistId: params.check, itemId: undefined })

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++
                if (item.message) resume.n++
                if (item.alerts && item.alerts.issues.length > 0) resume.is++
                if (item.alerts && item.alerts.issues.length == 0) resume.a++
                if (!item.apply) resume.na++
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            timeframe_id: state.timeframes.current
        }

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'unvalidate' })
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/task/unvalidate', paramsApi)
                .then(function (response) {
                    if (!response.status && context.rootGetters.getOffline) {
                        paramsApi.action = 'unvalidate'
                        return context
                            .dispatch('syncAction', paramsApi)
                            .then(() => {
                                return true
                            })
                            .catch(() => {
                                return true
                            })
                    }
                })
                .catch(() => {
                    if (context.rootGetters.getOffline) {
                        paramsApi.action = 'unvalidate'
                        return context
                            .dispatch('syncAction', paramsApi)
                            .then(() => {
                                return true
                            })
                            .catch(() => {
                                return true
                            })
                    }
                })
        } else if (context.rootGetters.getOffline) {
            paramsApi.action = 'unvalidate'
            return context
                .dispatch('syncAction', paramsApi)
                .then(() => {
                    return true
                })
                .catch(() => {
                    return true
                })
        }
    },
    complete(context, params) {
        context.dispatch('calcScore', { checklistId: params.checklist, itemId: undefined })

        let check = context.getters.getById(params.checklist)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        const canWorkOffline = context.rootGetters.getOffline

        let paramsApi = {
            checklist: params.checklist,
            complete_date: params.complete_date,
            resume: JSON.stringify(resume),
            values_not_visible: check.values_no_visible
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/complete', paramsApi)
                .then(function (response) {
                    let screenlockData = context.rootGetters['login/getScreenlockData']
                    let screenlockFlag = context.rootGetters['getScreenlockFlag']
                    if (screenlockFlag && params.checklist == screenlockData.checklist_id) {
                        context.dispatch('login/getScreenlockTasks', null, { root: true })
                    }
                    return response
                })
                .catch((error) => {
                    console.error('Error in complete():', error)
                    return { status: false }
                })
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'complete'
                let screenlockData = context.rootGetters['login/getScreenlockData']
                let screenlockFlag = context.rootGetters['getScreenlockFlag']
                if (screenlockFlag && paramsApi.checklist == screenlockData.checklist_id) {
                    context.commit('login/setScreenlockData', { checklist_id: false, message: '' }, { root: true })
                }
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        return { status: false }
                    })
                    .catch(() => {
                        return { status: false }
                    })
            } else {
                return { status: false }
            }
        }
    },
    uncomplete(context, params) {
        const canWorkOffline = context.rootGetters.getOffline
        let paramsApi = {
            checklist: params.checklist
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/uncomplete', paramsApi)
                .then(function (response) {
                    let screenlockFlag = context.rootGetters['getScreenlockFlag']
                    if (screenlockFlag && params.config.screenlock) {
                        let screenlock = {
                            checklist_id: params.checklist,
                            message: params.config.screenlockMessage
                        }
                        context.commit('login/setScreenlockData', screenlock, { root: true })
                    }
                    return response
                })
                .catch((error) => {
                    console.error(error)
                    return { status: false }
                })
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'uncomplete'
                let screenlockFlag = context.rootGetters['getScreenlockFlag']
                if (screenlockFlag && params.config.screenlock) {
                    let screenlock = {
                        checklist_id: params.checklist,
                        message: params.config.screenlockMessage
                    }
                    context.commit('login/setScreenlockData', screenlock, { root: true })
                }
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        return { status: false }
                    })
                    .catch(() => {
                        return { status: false }
                    })
            } else {
                return { status: false }
            }
        }
    },
    loadById(context, params) {
        if (process.env.VUE_APP_IS_APP === 'true' && fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.get('checklist/id/' + params.id, {}).then(function (response) {
                return context.dispatch('mergeOffline', { data: response.data }).then(function (response2) {
                    return context.dispatch('format', { data: response2, append: true }).then(() => {
                        return context.dispatch('calcScore', { checklistId: params.id, itemId: undefined })
                    })
                })
            })
        }
    },
    async answer(context, params) {
        const checklistId = params.check
        const itemId = params.answer

        let answerValue = params.value

        const canWorkOffline = context.rootGetters.getOffline

        // In case of image or signature type, JSON-parse the value
        if (state.config.item.itemsImage.includes(params.answer_type)) {
            answerValue = JSON.stringify(answerValue)
        }

        // answer
        context.commit('setAnswer', {
            check: checklistId,
            answer: itemId,
            emp: params.emp,
            date: params.date,
            value: params.value,
            bReadingFromSensor: params.bReadingFromSensor,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
        })

        // calculate score
        await context.dispatch('calcScore', { checklistId: checklistId, itemId: itemId })


        let checklist = context.getters.getById(checklistId)
        let total = checklist.total
        let answered = checklist.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in checklist.items) {
            let item = checklist.items[id]
            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        const paramsApi = {
            checklist: checklistId,
            task: itemId,
            employee: params.emp,
            complete_date: params.date,
            value: answerValue,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            timeframe_id: state.timeframes.current,
            type: params.answer_type,
            bReadingFromSensor: params.bReadingFromSensor
        }

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'answer' })
        }

        const isEmailAnswer = Number(params.answer_type) === FORMAT.EMAIL
        const syncOnAnswerFailure = canWorkOffline && !isEmailAnswer

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/answer', paramsApi)

            if (!response.status) {
                if (syncOnAnswerFailure && shouldQueueOfflineSync(response)) {
                    paramsApi.action = 'answer'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        console.error('Error in answer():', error)
                    }
                    return context.dispatch('calcScore', { checklistId: checklistId, itemId: itemId }).then(() => {
                        return { status: false }
                    })
                }

                context.commit('setReset', { check: checklistId, answer: itemId })
                return context.dispatch('calcScore', { checklistId: checklistId, itemId: itemId }).then(() => {
                    return { status: false, msg: response.msg }
                })
            }

            let answerValue = response.data.value
            if (state.config.item.itemsImage.includes(params.answer_type)) {
                answerValue = splitCommasBase64(answerValue)
                context.commit('setAnswer', {
                    check: checklistId,
                    answer: itemId,
                    emp: params.emp,
                    date: params.date,
                    value: answerValue,
                    bReadingFromSensor: params.bReadingFromSensor
                })
            }
            return response
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'answer'
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        return context.dispatch('calcScore', { checklistId: checklistId, itemId: itemId }).then(() => {
                            return { status: false }
                        })
                    })
                    .catch((error) => {
                        console.error('Error in answer():', error)
                        return context.dispatch('calcScore', { checklistId: checklistId, itemId: itemId }).then(() => {
                            return { status: false }
                        })
                    })
            } else {
                context.commit('setReset', { check: checklistId, answer: itemId })
                return context.dispatch('calcScore', { checklistId: checklistId, itemId: itemId }).then(() => {
                    return { status: false }
                })
            }
        }
    },
    async apply(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)
        let item = state.items[params.check].items[params.answer]
        let items = context.getters.getItemsBySection(params.check, params.answer)
        const canWorkOffline = context.rootGetters.getOffline

        // apply
        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.apply == 0 ? params.date : false,
            apply: params.apply,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
        })

        if (item.type == FORMAT.TITLE) {
            Object.keys(items).map(function (item_id, i) {
                let it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', { check: params.check, answer: it.item_id, date: params.apply == 0 ? params.date : false, apply: params.apply })
                }
            })
        }

        // calculate score
        await context.dispatch('calcScore', { checklistId: params.check, itemId: params.answer })

        // reset child items
        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                let item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // calculate score

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            apply: params.apply,
            total: total,
            answered: answered,
            score: score,
            timeframe_id: state.timeframes.current,
            resume: JSON.stringify(resume)
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/task/apply', paramsApi)
                .then(function (response) {
                    if (!response.status && context.rootGetters.getOffline) {
                        paramsApi.action = 'apply'
                        return context.dispatch('syncAction', paramsApi)
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
                    }
                })
                .catch(() => {
                    if (context.rootGetters.getOffline) {
                        paramsApi.action = 'apply'
                        return context.dispatch('syncAction', paramsApi)
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
                    }
                })
        } else {
            if (context.rootGetters.getOffline) {
                paramsApi.action = 'apply'
                return context.dispatch('syncAction', paramsApi)
            } else {
                context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
            }
        }
    },
    async applyrecurrent(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)

        // apply
        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.apply == 0 ? params.date : false,
            apply: params.apply
        })

        var item = state.items[params.check].items[params.answer]
        if (item.type == FORMAT.TITLE) {
            let items = context.getters.getItemsBySection(params.check, params.answer)

            Object.keys(items).map(function (item_id, i) {
                var it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', { check: params.check, answer: it.item_id, date: params.apply == 0 ? params.date : false, apply: params.apply })
                }
            })
        }

        // calculate score
        await context.dispatch('calcScore', { checklistId: params.check, itemId: params.answer })

        // reset child items
        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // calculate score

        let check = context.getters.getById(params.check)
        let total = check.total
        let answered = check.completed
        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        return api
            .post('checklist/task/applyrecurrent', {
                checklist: params.check,
                task: params.answer,
                employee: params.emp,
                complete_date: params.date,
                apply: params.apply,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {
                if (!response.status) {
                    context.commit('setReset', { check: params.check, answer: params.answer })
                }
            })
    },
    async reset(context, params) {
        const canWorkOffline = context.rootGetters.getOffline

        if (context.rootGetters.getOffline || fnCheckConnection()) {
            let subtask = context.getters.getSubtasks(params.check, params.answer)
            const sync = !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline

            context.commit('setReset', {
                check: params.check,
                answer: params.answer,
                requireSync: sync,
                emp: params.emp,
                date: params.date
            })

            await context.dispatch('calcScore', { checklistId: params.check, itemId: params.answer })

            if (subtask.length > 0) {
                Object.keys(subtask).map((item_id, i) => {
                    var item = subtask[item_id]
                    context.commit('setReset', { check: params.check, answer: item, requireSync: sync })
                })
            }

            let check = context.getters.getById(params.check)
            let total = check.total
            let answered = check.completed
            let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
            let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

            for (let id in check.items) {
                let item = check.items[id]

                if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                    if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                    if (!item.apply) resume.na++ // if item has not notes
                }
            }

            const paramsApi = {
                checklist: params.check,
                task: params.answer,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume),
                timeframe_id: state.timeframes.current
            }

            if (canWorkOffline) {
                await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'reset' })
            }

            if (fnCheckConnection() && context.rootGetters.getFastConnection) {
                const { response } = await postTaskWithRetry(api, 'checklist/task/reset', paramsApi)

                if (!response.status) {
                    paramsApi.action = 'reset'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        console.error('Error in reset():', error)
                    }
                }

                return { status: true }
            } else {
                paramsApi.action = 'reset'
                return context
                    .dispatch('syncAction', paramsApi)
                    .then((response) => {
                        return { status: true }
                    })
                    .catch(() => {
                        return { status: true }
                    })
            }
        } else {
            return { status: false }
        }
    },
    async format({ state, context, commit, dispatch, rootState, rootGetters }, params = {}) {
        // TODO APPEND
        var append = params.append ? true : false
        let screenlockData = rootGetters['login/getScreenlockData']
        if (typeof params.screenlock == 'undefined' && screenlockData.checklist_id) {
            return true
        }

        // SETTER
        var aFrames = {}
        var aItems = {}

        // if (append) {
        //   params.data = params.append;
        // }

        if (typeof params.data !== 'undefined') {
            // Recopilar todos los template_id presentes en la respuesta
            const _templateIds = new Set()
            Object.values(params.data).forEach((tframe) => {
                if (tframe && tframe.templates) {
                    Object.values(tframe.templates).forEach((list) => {
                        if (list && list.template_id) _templateIds.add(list.template_id)
                    })
                }
            })

            // Cargar todos los templates en paralelo y construir el mapa custom indexado por templateId
            const _itemCustomMap = {}
            await Promise.all(
                [..._templateIds].map(async (tplId) => {
                    try {
                        const raw = await db_templates.getItem(tplId)
                        if (!raw) return
                        const tpl = typeof raw === 'string' ? JSON.parse(raw) : raw
                        if (tpl) _itemCustomMap[tplId] = buildItemCustomMapFromTemplates({ [tplId]: tpl })
                    } catch (e) {
                        // no-op: si no se puede leer el template, continuar sin custom
                    }
                })
            )

            Object.keys(params.data).map(function (item_id, i) {
                var tframe = params.data[item_id]

                tframe.completed = 0
                tframe.total = 0
                tframe.score = 0

                tframe.alert_pending_count = 0
                tframe.alert_pending_show = false
                tframe.alert_important_show = false

                const toLocationMoment = (value) => {
                    if (!value) return false
                    return moment.isMoment(value) ? value : toLocationWallClockMoment(rootGetters, value)
                }
                Object.keys(tframe.templates).map(function (item_id, i) {
                    var list = tframe.templates[item_id]

                    list.business_date = toLocationMoment(list.business_date)
                    list.created_date = toLocationMoment(list.created_date)
                    list.complete_date = toLocationMoment(list.complete_date)

                    // Timezone set TASKS
                    if (list.schedule) {
                        list.schedule['start'] = toLocationMoment(list.schedule.start)
                        list.schedule['end'] = toLocationMoment(list.schedule.end)
                    }
                    if (list.limit) {
                        list.limit['start'] = toLocationMoment(list.limit.start)
                        list.limit['end'] = toLocationMoment(list.limit.end)
                    }

                    list.def = state.config
                    list.timeframe_id = tframe.id
                    list.uncompleted = 0
                    list.completed = 0
                    list.unanswered = 0
                    list.total = 0
                    list.score = -1

                    list.need_justification = false

                    // Loop for all items
                    const _tplItemMap = _itemCustomMap[list.template_id] || {}
                    Object.keys(list.items).map(function (item_id, i) {
                        var item = list.items[item_id]

                        if ((!item.custom || item.custom.length === 0) && _tplItemMap[item_id]) {
                            item.custom = JSON.parse(JSON.stringify(_tplItemMap[item_id]))
                        } else if (_tplItemMap[item_id]) {
                            mergeTemplateCustomIntoItem(item, _tplItemMap[item_id])
                        }

                        // Timezone set
                        item.complete_date = toLocationMoment(item.complete_date)
                        if (item.review) {
                            item.review['complete_date'] = toLocationMoment(item.review.complete_date)
                        }
                        // Timezone set TASKS
                        item.planned_from_date = toLocationMoment(item.planned_from_date)
                        item.planned_to_date = toLocationMoment(item.planned_to_date)

                        // If type image - Split value to array
                        if (state.config.item.itemsImage.includes(item.type)) {
                            item.value = item.value && typeof item.value === 'string' ? splitCommasBase64(item.value) : item.value
                        }

                        item.complete_date = item.complete_date
                        item.available = 0
                        item.apply = parseInt(item.apply) == 1

                        item.config = typeof item.config === 'string' ? JSON.parse(item.config) : item.config

                        // If can pospone
                        if (item.margin > 0 && state.config.item.can_pospone) {
                            let now = getLocationMoment(rootGetters).set({ m: 0, s: 0, ms: 0 })
                            let start = moment(list.business_date).set({ m: 0, s: 0, ms: 0 })
                            if (item.planned_from_date) {
                                start = moment(item.planned_from_date).set({ m: 0, s: 0, ms: 0 })
                                // } else if (item.planned_from_date) {
                                //   start = moment(item.planned_from_date).set({m: 0,s: 0,ms: 0 });
                            }
                            let end = moment(start).add(parseInt(item.margin), 'd')

                            if (now.isBetween(start, end) && now.isAfter(start)) {
                                start = now
                            }

                            item.available = Math.round(moment.duration(end.diff(list.business_date)).asDays(), 0)
                        }

                        list.items[item_id] = { ...item }
                        Vue.set(list.items, item_id, { ...item })
                    })

                    aItems[list.id] = list
                })

                // tframe.templates = Object.keys(tframe.templates)

                // SETTER
                aFrames[tframe.id] = tframe
                // state.timeframes.data[tframe.id] = tframe;
            })
        }
        if (append) {
            aFrames = { ...aFrames, ...state.timeframes.data }
            aItems = { ...aItems, ...state.items }
        }

        // SETTER
        commit('setFormat', { items: aItems })
        commit('setTimeframes', { frames: aFrames })
        return true
    },
    calcScore({ state, context, commit, dispatch, rootState, rootGetters }, payload) {
        const { checklistId, itemId } = payload
        const checklists = state.items

        const timeframesData = state.timeframes.data
        const timeframeId = typeof checklistId === 'undefined' ? false : checklists[checklistId].timeframe_id
        const alertPendingCount = {}
        const alertPendingShow = {}
        const alertImportantShow = {}
        resetTimeframesForCalcScore(timeframesData, checklistId, timeframeId, alertPendingCount, alertPendingShow, alertImportantShow)

        const aCategories = state.categories
        const categoryId = typeof checklistId === 'undefined' ? false : checklists[checklistId].category_id
        const categoryAlertPendingCount = {}
        const categoryAlertPendingShow = {}
        const categoryAlertImportantShow = {}
        resetCategoriesForCalcScore(aCategories, checklistId, categoryId, categoryAlertPendingCount, categoryAlertPendingShow, categoryAlertImportantShow)

        Object.keys(checklists).map((currentChecklistId) => {
            const checklist = checklists[currentChecklistId]

            if (typeof checklistId === 'undefined' || currentChecklistId === checklistId) {
                const now = getLocationMoment(rootGetters).set({ s: 0, ms: 0 })
                const pastScore = checklist.score ? checklist.score : 0

                checklist.def = state.config
                checklist.uncompleted = 0
                checklist.completed = 0
                checklist.unanswered = 0
                checklist.total = 0
                checklist.score = 0
                checklist.values_no_visible = false
                checklist.justify_id = checklist.justify_id ? checklist.justify_id : checklist.justify_msg ? '0' : false
                checklist.planned_items = 0
                checklist.to_review = 0
                checklist.current_day = checklist.business_date.format('YYYY-MM-DD') == now.format('YYYY-MM-DD')
                checklist.in_time = checklist.current_day && (checklist.timeframe_id == 0 || (now.valueOf() >= checklist.schedule.start.valueOf() && now.valueOf() <= checklist.schedule.end.valueOf()))
                checklist.completed_in_time = checklist.complete_date && (checklist.timeframe_id == 0 || checklist.complete_date.valueOf() <= checklist.schedule.end.valueOf())
                checklist.items = buildHierarchicalOrderedItems(checklist.items)

                const orderedItems = Object.values(checklist.items)

                // loop through all items of a checklist
                orderedItems.forEach((item) => {
                    // setea item.is_visible (determina si el item se muestra o no)
                    // setea item.order (determina el orden de items, subitems y subsubitems)
                    item.is_visible = true
                    item.order = getBaseOrder(item)

                    const parent = item.parent_id ? checklist.items[item.parent_id] : null
                    item.is_subsubitem = !!(parent && parent.parent_id)

                    // SUBITEM / SUBSUBITEM
                    if (typeof item.conditions !== 'undefined' && item.conditions != null && item.conditions) {
                        const subitemConditionsResult = computeCalcScoreSubitemConditions(item, checklist)
                        item.is_visible = subitemConditionsResult.is_visible
                        item.order = subitemConditionsResult.order
                    }

                    if (!checklist.values_no_visible && item.value !== null && !item.is_visible) {
                        checklist.values_no_visible = true
                    }
                    // ALERTS / INCIDENTS / ACTION PLANs
                    const shouldComputeItemAlerts = item.alerts_triggers && (itemId == undefined || itemId == item.item_id)
                    // solo entra por este bloque si:
                    // - el item tiene alerts_triggers
                    // - itemId es undefined (para cuando se evalua toda la lista)
                    // - itemId es igual al item_id del item actual en el loop
                    if (shouldComputeItemAlerts) {
                        // item.alerts: determina si debe mostrar una alerta o no (puede ser un popup de alerta, una incidencia o un plan de acción)
                        item.alerts = false
                        const oAlert = computeCalcScoreItemAlerts(item)

                        if (oAlert !== null) {
                            item.alerts = oAlert
                            Vue.set(item, 'alerts', oAlert)
                        }
                    }

                    // determinar si el item requiere revisión
                    if (item.require_review && !item.review && item.apply && !item.planned_to_date) {
                        checklist.to_review++
                    }

                    const scoringDeltas = computeCalcScoreItemScoringDeltas(item, checklist)
                    checklist.unanswered += scoringDeltas.unanswered
                    checklist.completed += scoringDeltas.completed
                    checklist.total += scoringDeltas.total

                    if (item.planned_to_date) {
                        checklist.planned_items++
                    }
                })

                let divisor = parseInt(checklist.total + checklist.planned_items) == 0 ? 1 : parseInt(checklist.total + checklist.planned_items)

                checklist.score = Math.round((parseFloat(checklist.completed + checklist.planned_items) * 100) / divisor)

                if (pastScore != -1 && pastScore < 100 && checklist.score >= 100) {
                    dispatch('complete', { checklist: checklist.id, complete_date: moment().unix(), values: [] })
                    checklist.complete_date = moment()
                } else if (pastScore != -1 && pastScore >= 100 && checklist.score < 100) {
                    dispatch('uncomplete', { checklist: checklist.id, config: checklist.config })
                    checklist.complete_date = false
                }

                const limitStart = moment(checklist.limit.start)
                const limitEnd = moment(checklist.limit.end)
                const scheduleStart = moment(checklist.schedule.start)
                const scheduleEnd = moment(checklist.schedule.end)

                // determinar si el checklist requiere justificación
                const needJustificationResult = computeCalcScoreNeedJustification(now, checklist, limitStart, limitEnd)
                checklist.need_justification = needJustificationResult.needJustification

                if (needJustificationResult.needJustification) {
                    alertPendingCount[checklist.timeframe_id]++
                    if (checklist.category_id && aCategories[checklist.category_id]) {
                        categoryAlertPendingCount[checklist.category_id]++
                    }
                    if (aCategories['all']) {
                        categoryAlertPendingCount['all']++
                    }

                    if (needJustificationResult.bumpPendingShow) {
                        alertPendingShow[checklist.timeframe_id] = true
                        if (checklist.category_id && aCategories[checklist.category_id]) {
                            categoryAlertPendingShow[checklist.category_id]++
                        }
                        if (aCategories['all']) {
                            categoryAlertPendingShow['all']++
                        }
                    }
                }

                if (now.isBetween(limitStart, limitEnd) && !now.isBetween(scheduleStart, scheduleEnd) && now.isAfter(scheduleEnd) && checklist.important && parseInt(checklist.score) < 100) {
                    alertImportantShow[checklist.timeframe_id] = parseInt(checklist.score) < 100
                    if (checklist.category_id && aCategories[checklist.category_id]) {
                        categoryAlertImportantShow[checklist.category_id]++
                    }
                    if (aCategories['all']) {
                        categoryAlertImportantShow['all']++
                    }

                    checklist.important = parseInt(checklist.score) < 100
                }

                addChecklistTotalsToTimeframeAndCategoryAggregates(checklist, timeframesData, aCategories)

                checklist.items = reorderChecklistItemsByOrderValue(checklist)

                checklists[checklist.id] = { ...checklist }
            }

            if (typeof checklistId !== 'undefined' && currentChecklistId != checklistId && timeframeId !== false && checklist.timeframe_id == timeframeId) {
                addChecklistTotalsToTimeframeAndCategoryAggregates(checklist, timeframesData, aCategories)
            }
        })

        applyAlertMapsToTimeframesData(timeframesData, checklistId, timeframeId, alertPendingCount, alertPendingShow, alertImportantShow)
        applyAlertMapsToCategories(aCategories, checklistId, categoryId, categoryAlertPendingCount, categoryAlertPendingShow, categoryAlertImportantShow)

        commit('setScore', { items: checklists })
        commit('setTimeframes', { frames: timeframesData })
        commit('setCategories', aCategories)

        // BUILD-7281/BUILD-7290: calcScore es el único punto donde need_justification/unanswered
        // quedan al día (carga inicial, justificar, responder, o el watcher periódico de más
        // abajo), así que es el lugar natural para mantener sincronizados los KPIs locales de Radar.
        // BUILD-7404: solo se recalculan si `items` es de hoy; ver refreshLocalRadarKpisFromItems.
        refreshLocalRadarKpisFromItems(state, commit, rootGetters)
        ensureLocalRadarKpisWatcherStarted(dispatch)
    },
    loadStats(context, params) {
        context.commit('resetStats', {})
        var now = getLocationMoment(context.rootGetters)
        var currentDate = moment.utc(params.date)
        var items = state.items
        var timeframes = state.timeframes.data

        // SET ALERTS --------------------------------------------------------------
        // --> Left important (urgentes)
        var left_important = {
            state: 'error',
            value: 0,
            name: '',
            route: { name: 'Checklist', params: { withAlert: 'alert_important_show' } }
        }

        Object.keys(items).map(function (item_id, i) {
            var item = items[item_id]
            var stillAvailable = false

            // Left important tasks
            var limit = item.limit
            var schedule = item.schedule

            if (currentDate.isBetween(limit.start, limit.end) && !currentDate.isBetween(schedule.start, schedule.end) && currentDate.isAfter(schedule.end) && currentDate.isSame(now, 'day')) {
                stillAvailable = true
            }
            if (!item.need_justification && item.important && stillAvailable) {
                left_important.value++
            }
        })
        if (left_important.value > 0) {
            left_important.name = i18n.tc('home.tasks.left_important', left_important.value)
            context.commit('setStats', {
                where: 'alerts',
                key_name: 'left_important',
                values: left_important
            })
        }

        // --> Left pending (unjustified)
        var left_pending = {
            state: 'warning',
            value: 0,
            name: '',
            route: {
                name: 'Checklist',
                params: { propDate: moment(currentDate).subtract(1, 'day'), withAlert: 'alert_pending_show' }
            }
        }

        api.post('checklist/stats/unjustified', { date: currentDate.format('YYYY-MM-DD') }).then(function (response) {
            left_pending.value = response.data ? response.data : 0
            if (left_pending.value > 0) {
                left_pending.name = i18n.tc('home.tasks.left_pending', left_pending.value)
                context.commit('setStats', {
                    where: 'alerts',
                    key_name: 'left_pending',
                    values: left_pending
                })
            }
        })

        // SET ACTIVITY ------------------------------------------------------------
        // -- Tasks done
        var tfCompleted = 0
        var tfTotal = 0
        var tfScore = 0

        Object.keys(timeframes).map(function (item_id, i) {
            var tf = timeframes[item_id]

            tf.score = isNaN(tf.score) ? 0 : tf.score
            tfCompleted += tf.completed
            tfTotal += tf.total
        })

        if (tfCompleted && tfTotal) {
            tfScore = Math.round((tfCompleted / tfTotal) * 100, 0)
        }
        context.commit('setStats', {
            where: 'activity',
            key_name: 'done',
            values: {
                name: i18n.t('home.activity.tasks_done'),
                value: tfScore + '%'
            }
        })
    },
    completeAll(context, params) {
        return api
            .post('checklist/answer', {
                checklist_id: params.check,
                employee: params.emp
            })
            .then(function (response) {
                var items = context.getters['getTasks'](params.check)

                Object.keys(items).map(function (id, i) {
                    var val = items[id]

                    if (val.type == 1 && !val.value && !val.parent_id) {
                        context.commit('setAnswer', {
                            check: params.check,
                            answer: val.item_id,
                            emp: params.emp,
                            date: params.date,
                            value: 1
                        })
                    }
                })
                context.dispatch('calcScore', { checklistId: params.check, itemId: undefined })

                return response
            })
    },
    resetAll(context, params) {
        return api
            .post('checklist/reset', {
                checklist_id: params.check,
                employee: params.emp
            })
            .then(function (response) {
                var items = context.getters['getTasks'](params.check)

                Object.keys(items).map(function (id, i) {
                    var val = items[id]

                    if (val.type == 1 && val.value && !val.parent_id) {
                        context.commit('setReset', {
                            check: params.check,
                            answer: val.item_id
                        })
                    }
                })
                context.dispatch('calcScore', { checklistId: params.check, itemId: undefined })

                return response
            })
    },
    checkDependencyTpl(context, params) {
        var result = false
        var templates = { ...state.items }
        Object.keys(templates).map(function (id, i) {
            var val = templates[id]
            if (val.template_id == params.tpl) {
                if (!val.complete_date) {
                    result = val
                }
            }
        })

        return result
    },
    recoverValues(context, params) {
        // TODO APPEND
        return api.post('checklist/recover/' + params.check, {}).then(function (response) {
            if (response.status) {
                return context.dispatch('format', { data: response.data }).then(function () {
                    context.dispatch('calcScore', { checklistId: params.id, itemId: undefined })
                    return response.data
                })
            } else {
                router.go()
            }

            return response.data
        })
    },
    async justify(context, params) {
        params.justify_date = getLocationMoment(context.rootGetters).format('DD/MM/YYYY HH:mm:ss')

        // Se captura antes de justificar: template_id y business_date no cambian al justificar,
        // pero los necesitamos para decidir si el template deja de contar en el KPI del Radar.
        // El KPI "tasks_to_justify_yesterday" solo aplica cuando el checklist justificado es,
        // efectivamente, de ayer (justificar un checklist de hoy no debe tocar ese KPI).
        // "Ayer" se calcula con el reloj operacional del local y no con la zona del dispositivo.
        const justifiedChecklist = state.items[params.check]
        const yesterdayBusinessDateKey = getYesterdayBusinessDateKey(context.rootGetters)
        const justifiedChecklistIsFromYesterday = getChecklistBusinessDateKey(justifiedChecklist) === yesterdayBusinessDateKey

        // TODO
        const canWorkOffline = context.rootGetters.getOffline
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/update', {
                    checklist: params.check,
                    reason: params.reason,
                    description: params.description
                })
                .then(function (response) {
                    // Quien llama a esta acción (p.ej. detail.vue) no manda justify_user_id, así que
                    // sin esto quedaba `undefined`: isChecklistJustified lo trataba como NO
                    // justificado y el checklist nunca salía de los KPIs de Radar tras justificar.
                    const user = context.rootState.loginUser.local.user
                    context.commit('setJustified', {
                        check: params.check,
                        reason_id: params.reason,
                        reason_msg: params.description,
                        justify_user_id: user.id,
                        justify_date: params.justify_date
                    })
                    if (justifiedChecklistIsFromYesterday) {
                        updateYesterdayKpiAfterJustify(context, state, context.rootGetters, yesterdayBusinessDateKey)
                    }
                    context.dispatch('calcScore', { checklistId: params.check, itemId: undefined })

                    return response
                })
        } else {
            if (canWorkOffline) {
                const user = context.rootState.loginUser.local.user

                context.dispatch('syncAction', {
                    action: 'update',
                    checklist: params.check,
                    reason: params.reason,
                    description: params.description,
                    employee: user.id,
                    complete_date: params.justify_date,
                    timeframe_id: state.timeframes.current
                })

                context.commit('setJustified', {
                    check: params.check,
                    reason_id: params.reason,
                    reason_msg: params.description,
                    justify_user_id: user.id,
                    justify_date: params.justify_date
                })
                if (justifiedChecklistIsFromYesterday) {
                    updateYesterdayKpiAfterJustify(context, state, context.rootGetters, yesterdayBusinessDateKey)
                }

                context.dispatch('calcScore', { checklistId: params.check, itemId: undefined })

                await db_tasks.getItem(state.timeframes.current).then(async function (item) {
                    item.templates[params.check].justify_id = params.reason
                    item.templates[params.check].justify_msg = params.description
                    item.templates[params.check].justify_user_id = user.id
                    item.templates[params.check].justify_date = params.justify_date
                    await db_tasks.setItem(state.timeframes.current, item)
                })
            } else {
            }
        }
    },
    async postpone({ rootState, commit, dispatch, getters }, params) {
        let loggeduser = rootState.loginUser.local.user

        commit('setPostposed', {
            check: params.check,
            answer: params.answer,
            date: params.date,
            reason_id: params.reason,
            reason_msg: params.description,
            employee: loggeduser.id
        })

        await dispatch('calcScore', params.check)

        let check = getters.getById(params.check)
        let total = check.total + check.planned_items
        let answered = check.completed + check.planned_items

        let score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        let resume = { it: 0, is: 0, a: 0, n: 0, na: 0 }

        for (let id in check.items) {
            let item = check.items[id]

            if (getCustomApply(item) !== 0 && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        return api
            .post('checklist/task/plan', {
                checklist: params.check,
                task: params.answer,
                date: params.date,
                reason: params.reason,
                description: params.description,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {
                return response
            })
    },
    updateIssue(context, params) {
        let item = context.getters['getTaskById'](params.check, params.answer)

        if (item) {
            let value = item.reference ? item.reference : false

            if (params.status) {
                value.status = params.status
            }
            if (params.toDelete) {
                value = false
            }

            context.commit('setAnswerProp', {
                check: params.check,
                answer: params.answer,
                prop: 'reference',
                value: value
            })
        }
    },
    syncAction(context, params) {
        params.queued_date = moment().valueOf()
        params.id = moment().valueOf()

        return context.dispatch('checklist/addTaskToQueue', params, { root: true }).then(() => {
            return { status: true }
        })
    },
    sync(context, queue) {
        let checklists = {}
        for (let key in queue) {
            let queueElement = queue[key]
            if (typeof checklists[queueElement.checklist] === 'undefined') {
                checklists[queueElement.checklist] = {
                    items: [],
                    resume: { it: 0, is: 0, a: 0, n: 0, na: 0 },
                    total: 0,
                    answered: 0,
                    score: 0
                }
            }

            checklists[queueElement.checklist].resume = queueElement.resume
            checklists[queueElement.checklist].total = queueElement.total
            checklists[queueElement.checklist].answered = queueElement.answered
            checklists[queueElement.checklist].score = queueElement.score
            checklists[queueElement.checklist].items.push(queueElement)
        }
        return api
            .post(`checklist/${state.type}/sync`, { checklists: JSON.stringify(checklists) })
            .then(function (response) {
                if (context.rootGetters['getScreenlockFlag']) context.dispatch('login/getScreenlockTasks', null, { root: true })
                if (response.data) {
                    const issues = response.data.filter((queue) => queue.issue_id)
                    if (issues.length > 0) {
                        context.dispatch('updateIssueOfflineDatabase', issues)
                        response.data = response.data.filter((queue) => !queue.issue_id)
                    }
                }
                return context
                    .dispatch('updateOfflineDatabase', response)
                    .then(() => {
                        return response
                    })
                    .catch(function (error) {
                        logError(error)
                        return false
                    })
            })
            .catch(function (error) {
                logError(error)
                return false
            })
    },
    async mergeOffline(context, params = {}) {
        await db_tasks_queue.iterate(function (answer, checklistId, iterationNumber) {
            const { timeframe_id, checklist, task } = answer

            if (typeof params.data[timeframe_id] !== 'undefined' && typeof params.data[timeframe_id].templates[checklist] !== 'undefined' && typeof params.data[timeframe_id].templates[checklist].items[task] !== 'undefined') {
                params.data[timeframe_id].templates[checklist].items[task].requireSync = true

                if (answer.action === 'answer' || answer.action === 'reset') {
                    params.data[timeframe_id].templates[checklist].items[task].employee = answer.employee
                    params.data[timeframe_id].templates[checklist].items[task].complete_date = answer.complete_date
                    params.data[timeframe_id].templates[checklist].items[task].value = FORMAT.PHOTO === task.type ? answer.value : answer.value.toString()
                }

                if (answer.action === 'validate') {
                    params.data[timeframe_id].templates[checklist].items[task].review = {
                        value: 1,
                        employee: context.rootGetters['employee/getEmployee'](answer.employee),
                        complete_date: moment(answer.queued_date)
                    }
                }

                if (answer.action === 'unvalidate') {
                    params.data[timeframe_id].templates[checklist].items[task].review = null
                }

                if (answer.action === 'comment' && typeof answer.file !== 'undefined' && answer.file) {
                    params.data[timeframe_id].templates[checklist].items[task].image = answer.file
                }
                if (answer.action === 'comment' && typeof answer.message !== 'undefined' && answer.message) {
                    params.data[timeframe_id].templates[checklist].items[task].message = answer.message
                }
            }
        })
        return params.data
    },
    async updateOfflineDatabase(context, response) {
        for (let index in response.data) {
            const { checklist, value, task, timeframe_id, template_id, type, image } = response.data[index]
            try {
                const offlineChecklists = await db_tasks.getItem(timeframe_id)

                if (type === 'answer') offlineChecklists.templates[checklist].items[task].value = value
                if (type === 'comment') offlineChecklists.templates[checklist].items[task].image = image
                //Para que no rompa la app
                if (timeframe_id) {
                    await db_tasks.setItem(timeframe_id, offlineChecklists)
                }
            } catch (error) {
                logError(error)
            }
        }
    },
    async updateIssueOfflineDatabase(context, response) {
        if (response) {
            const checklists = response
            checklists.forEach(async (checklist) => {
                //Actualizar el estado de sincronización de los registros
                await store.dispatch('issues/updateStateOfSync', checklist)

                // Actualizar el id de sincronización de los registros
                if (checklist && checklist.checklist_temporal && checklist.checklist) {
                    const register = await db_issues.getItem(checklist.checklist_temporal)
                    if (register) {
                        register.id = checklist.checklist
                        register.issue_id = checklist.issue_id
                        await db_issues.removeItem(checklist.checklist_temporal)
                        await db_issues.setItem(checklist.checklist, register)
                    }
                    // change the key of the item and id
                    const copy = store.getters['issues/getAll']
                    copy[checklist.checklist_temporal].id = checklist.checklist
                    copy[checklist.checklist_temporal].requireSync = false
                    copy[checklist.checklist_temporal].issue_id = checklist.issue_id
                    copy[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const updateItem = { [checklist.checklist]: copy[checklist.checklist_temporal] }
                    delete copy[checklist.checklist_temporal]

                    const allItems = { ...updateItem, ...copy }
                    /* context.state.items = {...copy} */
                    await store.dispatch('issues/format', { data: allItems })

                    // update allItems
                    const newIssueItem = store.getters['issues/getAll'][copy.status]
                    newIssueItem[checklist.checklist_temporal].id = checklist.checklist
                    newIssueItem[checklist.checklist_temporal].requireSync = false
                    newIssueItem[checklist.checklist_temporal].issue_id = checklist.issue_id
                    newIssueItem[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const newAllItems = { ...newIssueItem }
                    store.commit('issues/setAllItems', { items: newAllItems, type: copy.status })
                }
            })
        }
    },
    async clearOfflineQueue(context, params) {
        const data = params.data
        const action = params.action
        //['answer', 'reset', 'validate', 'unvalidate', 'complete', 'uncomplete']
        const mapActionsToRemove = {
            answer: ['answer', 'reset'],
            reset: ['answer', 'reset'],
            validate: ['validate', 'unvalidate'],
            unvalidate: ['validate', 'unvalidate'],
            comment: ['comment']
        }

        const actionsToRemove = mapActionsToRemove[action]

        await db_tasks_queue.iterate(async function (answer, key, iterationNumber) {
            const isTheSameChecklist = answer.checklist === data.checklist && answer.task === data.task
            let isGalleryItem = false
            if (data.type === FORMAT.PHOTO) {
                const dataArr = JSON.parse(data.value)
                isGalleryItem = dataArr.length > 1
            }
            if (actionsToRemove.includes(answer.action) && isTheSameChecklist && !isGalleryItem) {
                await db_tasks_queue.removeItem(key)
            }
        })
    },
    loadLastValueForItem(context, params) {
        return api.post(`checklist/task/last-value`, { item_id: params.item.item_id }).then((response) => {
            return response
        })
    }
}

// mutations
const mutations = {
    // Commonisimos, está en todos los modules
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    // Commons
    setDate(state, date) {
        Vue.set(state, 'date', date)
    },
    setAnswerProp(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var prop = payload.prop
        var value = payload.value

        state.items[check].items[answer][prop] = value
        Vue.set(state, 'items', { ...state.items })
    },
    setComment(state, payload) {
        let check = payload.check
        let answer = payload.answer
        let aux = state.items[check].items[answer]

        aux.message = payload.message
        aux.image = payload.image ? payload.image : false
        aux.requireSync = payload.requireSync

        Vue.set(state.items[check].items, answer, { ...aux })
        Vue.set(state, 'items', { ...state.items })
    },
    setAnswer(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var value = payload.value
        var date = payload.date
        var bReadingFromSensor = payload.bReadingFromSensor

        state.items[check].items[answer].value = value
        state.items[check].items[answer].requireSync = payload.requireSync
        state.items[check].items[answer].readingFromSensor = bReadingFromSensor
        if (date) {
            var item = state.items[check].items[answer]
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) state.items[check].items[answer].employee = emp

        Vue.set(state, 'items', { ...state.items })
    },
    setApply(state, payload) {
        const check = payload.check
        const answer = payload.answer
        const emp = payload.emp
        const apply = payload.apply
        const date = payload.date

        state.items[check].items[answer].apply = apply
        state.items[check].items[answer].requireSync = payload.requireSync

        if (date) {
            state.items[check].items[answer].complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
            state.items[check].items[answer].value = null
        } else {
            state.items[check].items[answer].complete_date = false
        }

        if (emp) {
            state.items[check].items[answer].employee = emp
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setReset(state, payload) {
        var check = payload.check
        var answer = payload.answer
        const sync = payload.requireSync ? payload.requireSync : false
        let emp = payload.emp ? payload.emp : false
        let date = payload.date ? payload.date : false

        state.items[check].items[answer].complete_date = false
        state.items[check].items[answer].employee = false
        state.items[check].items[answer].review = false
        state.items[check].items[answer].value = null
        state.items[check].items[answer].requireSync = sync

        if (emp && date) {
            state.items[check].items[answer].reset = {
                e: emp,
                d: date
            }
        }

        Vue.set(state, 'items', { ...state.items })
    },
    setValidation(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var date = payload.date

        state.items[check].items[answer].review = {}
        state.items[check].items[answer].requireSync = payload.requireSync

        if (date) {
            var item = state.items[check].items[answer].review
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) {
            var employee = store.getters['employee/getEmployee'](emp)
            state.items[check].items[answer].review.employee = employee
        }
        state.items[check].items[answer].review.value = 1

        Vue.set(state, 'items', { ...state.items })
    },
    setPostposed(state, payload) {
        var check = payload.check
        var answer = payload.answer
        var date = payload.date
        var reason_id = payload.reason_id
        var reason_msg = payload.reason_msg
        let employee = payload.employee

        // todo set employee

        state.items[check].items[answer].planned_reason_id = reason_id
        state.items[check].items[answer].planned_reason_msg = reason_msg
        state.items[check].items[answer].planned_to_date = date
        state.items[check].items[answer].employee = employee

        Vue.set(state, 'items', { ...state.items })
    },
    setUnvalidation(state, payload) {
        var check = payload.check
        var answer = payload.answer

        state.items[check].items[answer].review = false
        state.items[check].items[answer].requireSync = payload.requireSync

        Vue.set(state, 'items', { ...state.items })
    },
    resetStats(state, payload) {
        state.stats.alerts = {}
        state.stats.activity = {}
    },
    setStats(state, payload) {
        state.stats[payload.where] = { ...state.stats[payload.where] }
        state.stats[payload.where][payload.key_name] = { ...payload.values }
    },
    // Common but may change in other tools
    setFormat(state, payload) {
        state.items = payload.items
        Vue.set(state, 'items', { ...payload.items })
    },
    setScore(state, payload) {
        state.items = payload.items
        Vue.set(state, 'items', { ...payload.items })
    },
    // Tasks
    setCurrentTimeframe(state, value) {
        state.timeframes.current = value
    },
    setJustified(state, payload) {
        var check = payload.check
        var reason_id = payload.reason_id
        var reason_msg = payload.reason_msg
        var justify_user_id = payload.justify_user_id
        var justify_date = payload.justify_date

        state.items[check].justify_id = reason_id
        state.items[check].justify_msg = reason_msg
        state.items[check].justify_user_id = justify_user_id
        state.items[check].justify_date = justify_date

        Vue.set(state, 'items', { ...state.items })
    },
    setTimeframes(state, payload) {
        state.timeframes.data = payload.frames
        Vue.set(state.timeframes, 'data', { ...payload.frames })
    },
    setCurrentDate(state, date) {
        state.user_date_changed = true
        Vue.set(state, 'current_date', date)
    },
    setCurrentDateFromLocation(state, date) {
        state.user_date_changed = false
        Vue.set(state, 'current_date', date)
    },
    setCategories(state, payload) {
        var categories = payload
        state.categories = { ...categories }
        Vue.set(state, 'categories', { ...categories })
    },
    setItem(state, payload) {
        var item = payload
        state.items[item.id] = { ...item }
        Vue.set(state, 'items', { ...state.items })
    },
    setRequireSync(state, payload) {
        let check = payload.check
        let answer = payload.answer

        if (answer && typeof state.items[check] !== 'undefined' && typeof state.items[check].items[answer] !== 'undefined') {
            state.items[check].items[answer].requireSync = payload.requireSync
            state.items[check].items[answer] = { ...state.items[check].items[answer] }
            Vue.set(state, 'items', { ...state.items })
        }
    },
    setIsUserTyping(state, isTyping) {
        state.isUserTyping = isTyping
    },
    setLastLoadUserAction(state, timestamp) {
        state.last_load_user_action = timestamp
    },
    setLastLoadCategoriesUserAction(state, timestamp) {
        state.last_load_categories_user_action = timestamp
    },
    setRadarKPIs(state, radarKPIs) {
        state.radarKPIs = preserveLocalRadarKpis(state.radarKPIs, radarKPIs, [RADAR_KPI_TASKS_TO_JUSTIFY_YESTERDAY, RADAR_KPI_TASKS_TO_JUSTIFY_TODAY, RADAR_KPI_TASKS_EXPIRING_SOON])
    },
    /** Reemplaza por completo el listado `templates` de un KPI 100% local del Radar (p.ej. tasks_to_justify_today, BUILD-7281). */
    setLocalRadarKpiTemplates(state, { kpiKey, templates }) {
        state.radarKPIs = setTemplatesForRadarKpi(state.radarKPIs, kpiKey, templates)
    },
    setTemplates(state, payload) {
        const templates = {}

        if (payload.data !== undefined) {
            Object.keys(payload.data).map((templateId, i) => {
                const tpl = { ...payload.data[templateId] }

                tpl.last_update = tpl.last_update ? (moment.isMoment(tpl.last_update) ? tpl.last_update : moment(tpl.last_update * 1000)) : false

                templates[tpl.id] = tpl
            })
        }

        Vue.set(state, 'templates', { ...templates })
    },
    setTemplatesLoaded(state, payload) {
        state.templatesLoaded = payload
    }
}

function loadTemplatesOffline(commit, dispatch, rootGetters) {
    let items = {}
    let count = 0
    return db_tasks
        .iterate(function (value, key, iterationNumber) {
            if (value && (value.id || value.id === 0)) {
                items[value.id] = value
                count++
            }
        })
        .then(() => {
            if (count > 0) {
                let date = getTodayBusinessDateKey(rootGetters)
                commit('setDate', date)
                dispatch('mergeOffline', { data: items }).then((response) => {
                    dispatch('format', { data: response }).then(() => {
                        dispatch('calcScore', { checklistId: undefined, itemId: undefined })
                    })
                })
            }
        })
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
